import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import _ from 'lodash'
import { openAIUrl, scheduledTasksUrl, preBuiltTasksUrl, adminPreBuiltTasksUrl, preferencesUrl } from 'urls'
import { getAxiosDefaultConfig } from '../../modules/utils'
import {
  createXHRStreamState,
  feedXHRStreamProgress,
  getXHRResponseText,
} from '../../modules/sseBuffer'
import { getUserCredits } from '../../actions/planActions'

const refreshCreditsAfterChat = (dispatch: (action: unknown) => unknown) => {
  void dispatch(getUserCredits())
  window.setTimeout(() => {
    void dispatch(getUserCredits())
  }, 2500)
}
import { discoverRunningBackfills, watchBackfill } from '../../actions/backFillActions'
import {
  detectBrowserTimezone,
  detectBrowserTimezoneOffsetMinutes,
} from '../../modules/timezone'
import {
  DEFAULT_AGENT_MODEL,
  FALLBACK_AGENT_MODEL_CATALOG,
  isImageModelInCatalog,
  isModelInCatalog,
  normalizeAgentModelCatalog,
  resolveEffortLevel,
} from './agentModels'

// Helper to safely read agent mode preference from localStorage
const getStoredAgentMode = (): boolean => {
  try {
    const stored = localStorage.getItem('payhelm_agent_mode_enabled')
    if (stored === null) return true
    return stored === 'true'
  } catch {
    return true
  }
}

// Helper to save agent mode preference to localStorage
const saveAgentMode = (enabled: boolean): void => {
  try {
    localStorage.setItem('payhelm_agent_mode_enabled', String(enabled))
  } catch {
    // Ignore localStorage errors
  }
}

// Helper to read effort level from localStorage (1-5, default 3). Effort levels
// (AI Chat 2.0) replace the old autonomy levels; we migrate any persisted value once.
const getStoredEffortLevel = (): number | null => {
  try {
    let stored = localStorage.getItem('payhelm_effort_level')
    if (stored === null) {
      // One-time migration from the old autonomy key. The migrated value must be
      // written to the new key right here: reconcileAgentSelection only persists
      // a level that *differs* from what is already in state, so a migrated value
      // the catalog accepts as-is is never saved — and the legacy key is gone by
      // then, so the next reload loses the preference entirely.
      const legacyLevel = parseInt(localStorage.getItem('payhelm_autonomy_level') ?? '', 10)
      if (Number.isInteger(legacyLevel)) {
        stored = String(legacyLevel)
        localStorage.setItem('payhelm_effort_level', stored)
      }
    }
    // Migration runs once either way, so the legacy key goes regardless.
    localStorage.removeItem('payhelm_autonomy_level')
    const level = parseInt(stored ?? '', 10)
    // Not range-checked here: the valid levels are whatever the catalog lists,
    // and rejecting against a local guess is how a server-side change gets lost.
    // reconcileAgentSelection settles it once a catalog arrives.
    return Number.isInteger(level) ? level : null
  } catch {
    return null
  }
}

// Helper to save effort level to localStorage
const saveEffortLevel = (level: number): void => {
  try {
    localStorage.setItem('payhelm_effort_level', String(level))
  } catch {
    // Ignore localStorage errors
  }
}

// Restored values are deliberately NOT validated against the bundled fallback
// list. The stored key can legitimately be a model this build has never heard of
// — papaya added it and the user picked it — and rejecting it here would demote
// them to Auto on every reload, with the demotion written straight back to
// localStorage. `reconcileAgentSelection` drops it once a real catalog is loaded.
const getStoredAgentModel = (): string => {
  try {
    const stored = localStorage.getItem('payhelm_agent_model')
    const migrated = stored === 'fable-5' ? 'fable-5.1' : stored
    return (typeof migrated === 'string' ? migrated.trim() : '') || DEFAULT_AGENT_MODEL
  } catch {
    return DEFAULT_AGENT_MODEL
  }
}

// AI image model preference (persisted separately from the chat/agent model).
const getStoredImageModel = (): string => {
  try {
    const stored = localStorage.getItem('payhelm_image_model')
    return (typeof stored === 'string' ? stored.trim() : '') || ''
  } catch {
    return ''
  }
}

const saveImageModel = (model: string): void => {
  try {
    localStorage.setItem('payhelm_image_model', model)
  } catch {
    // Ignore localStorage errors
  }
}

const saveAgentModel = (model: string): void => {
  try {
    localStorage.setItem('payhelm_agent_model', model)
  } catch {
    // Ignore localStorage errors
  }
}

/**
 * Drop a selection the live catalog does not offer.
 *
 * Sending a retired model key is not an error server-side — papaya's
 * `normalizeAgentModelKey` quietly substitutes its own default — so the user
 * would be billed and served a different tier than the one the UI shows.
 *
 * Called from both the catalog fetch and the DB-settings fetch because either
 * can land second. It is a no-op until a real catalog has loaded: validating
 * against the bundled fallback is exactly what used to discard a newly launched
 * model, so an unrecognized key is left alone rather than reset early.
 */
const reconcileAgentSelection = (state): void => {
  if (state.agentCatalog.status !== 'succeeded') return

  const catalog = state.agentCatalog.data
  if (!isModelInCatalog(catalog, state.agentModel)) {
    state.agentModel = catalog.defaultModel
    saveAgentModel(catalog.defaultModel)
  }
  if (!isImageModelInCatalog(catalog, state.imageModel)) {
    state.imageModel = catalog.defaultImageModel
    saveImageModel(catalog.defaultImageModel)
  }
  const level = resolveEffortLevel(catalog, state.effortLevel)
  if (level != null && level !== state.effortLevel) {
    state.effortLevel = level
    saveEffortLevel(level)
  }
}

type AgentSettingsPayload = {
  model: string
  effortLevel: number
  agentModeEnabled: boolean
}

const queueSaveAgentSettings = _.debounce(async (settings: AgentSettingsPayload) => {
  try {
    await axios.post(`${preferencesUrl}/agent_settings`, { settings }, getAxiosDefaultConfig())
  } catch (err) {
    console.error('[saveAgentSettings] Error:', err)
  }
}, 1000)

const persistAgentSettings = (settings: AgentSettingsPayload): void => {
  queueSaveAgentSettings(settings)
}

let agentSettingsFetchStarted = false

export const initialState = {
  account: {},
  chat: {},
  chatHistory: {
    data: [],
    status: 'idle',    // 'idle' | 'loading' | 'succeeded' | 'failed' | 'deleting' | 'updating'
    loadingMore: false,
    hasMore: false,
    offset: 0,
    error: null,
  },
  /** message_ids of conversations that currently have an in-progress / streaming turn.
   *  Shared so the chat history list (sidebar + full view) can show a loading
   *  indicator and the user can switch between chats while one is still loading. */
  activeChatIds: [] as string[],
  relatedQuestions: {},
  relatedQuestionsStatus: {},
  relatedQuestionsError: {},
  insightResponse: {},
  insightStatus: {},
  insightError: {},
  chatBoxIsExpanded: false,
  messageId: null,
  // Agent mode state - initialized from localStorage for persistence
  agentModeEnabled: getStoredAgentMode(),
  effortLevel: getStoredEffortLevel(), // 1-5, default 3 (replaces autonomyLevel)
  agentModel: getStoredAgentModel(), // 'auto' | 'haiku' | 'sonnet' | 'opus-4.7' | 'opus-4.8' | 'opus-5' | 'fable-5.1'
  imageModel: getStoredImageModel(), // AI image model for wordpress_generate_image (gpt-image-1 | gpt-image-1.5 | gpt-image-2)
  // Server-driven model/effort options (GET /openai/chat/agent/models). Seeded
  // with the bundled fallback so pickers render before the fetch resolves.
  agentCatalog: {
    data: FALLBACK_AGENT_MODEL_CATALOG,
    status: 'idle',
    error: null,
  },
  agentSteps: {},  // { [sourcePage]: AgentStep[] }
  agentKnowledgeSources: {}, // { [sourcePage]: KnowledgeSource[] } - cross-chat/task sources the agent read
  agentGeneratedPdfs: {}, // { [sourcePage]: GeneratedPdf[] } - PDFs the agent generated this turn (download cards)
  agentStatus: {}, // { [sourcePage]: 'idle' | 'running' | 'completed' | 'failed' }
  agentCurrentStep: {}, // { [sourcePage]: number }
  agentMemoryUpdate: {}, // { [sourcePage]: { message, savedChars, currentSize, timestamp } }
  agentNarrations: {}, // { [sourcePage]: Narration[] }
  agentAvailableTools: {}, // { [sourcePage]: string[] } - list of tool names from MCP
  agentCsuiteReviews: {},  // { [sourcePage]: CsuiteReviewStep[] } - C-suite executive reviews
  agentCsuiteStatus: {},   // { [sourcePage]: 'idle' | 'reviewing' | 'completed' }
  // Claude SDK session id per conversation: { [chatId]: sessionId }.
  // Echoed back on follow-up turns so the backend resumes the SDK session
  // instead of replaying the whole transcript as a fresh prompt. Keyed by chat
  // id (not sourcePage) so a new chat on the same page can't resume the old
  // thread. In-memory only — a page reload correctly falls back to replay.
  agentSessionIds: {},
  // #935: per-prompt credit breakdown for the latest assistant turn, keyed by
  // sourcePage. Streamed additional_content is otherwise discarded by
  // updateInsightChunk (which keeps only `data`), and the chat entry is built
  // from an explicit field list — so this has to live in state to reach it.
  agentCreditSummary: {},
  // Chat preferences for seamless conversation resumption
  chatPreferences: {
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    lastActiveChatId: null,
    autoLoadEnabled: true,
    interruptedSessions: [], // Array of interrupted sessions
    error: null,
  },
  /** User-defined HTTP tools for Agent mode */
  customTools: {
    items: [] as any[],
    status: 'idle' as 'idle' | 'loading' | 'succeeded' | 'failed',
    error: null as any,
  },
  // Scheduled tasks (from AI Chat)
  scheduledTasks: [] as any[],
  scheduledTasksStatus: 'idle' as 'idle' | 'loading' | 'succeeded' | 'failed',
  selectedTaskId: null as string | null,
  taskRuns: {} as Record<string, { runs: any[]; total: number }>,
  taskRunsStatus: 'idle' as 'idle' | 'loading' | 'succeeded' | 'failed',
  taskIntentSynthesis: null as any,
  taskIntentStatus: 'idle' as 'idle' | 'loading' | 'succeeded' | 'failed',
  /** Client-only key for last successful synthesize-intent (chat + message + tool); avoids redundant API calls when reopening the task panel. */
  taskIntentSynthesisKey: null as string | null,
  /** Key for the in-flight synthesize request (prevents duplicate POSTs while loading). */
  taskIntentSynthesisInFlightKey: null as string | null,
  /** Proactive scheduled-task suggestions keyed by assistant message id (uuid) */
  taskSuggestions: {} as Record<
    string,
    {
      loading?: boolean
      suggest: boolean
      taskName: string
      schedule: string
      reason: string
      dismissed?: boolean
    }
  >,
  // ---- Task Library: pre-built tasks ----
  preBuiltTasks: [] as any[],
  preBuiltTasksStatus: 'idle' as 'idle' | 'loading' | 'succeeded' | 'failed',
  preBuiltTasksError: null as any,
  preBuiltTaskBySlug: {} as Record<string, any>,
  preBuiltTaskDetailStatus: {} as Record<string, 'idle' | 'loading' | 'succeeded' | 'failed'>,
  preBuiltInstallStatus: 'idle' as 'idle' | 'installing' | 'succeeded' | 'failed',
  preBuiltInstallError: null as any,
  // Active pre-built task context: when a user clicks "Chat Now" on a Task Library
  // entry, the chosen task is stashed here so the AI page can prefill the composer
  // and forward a curated workflow hint into askAgentForInsight.
  activePreBuiltTaskContext: null as null | {
    id: string
    name: string
    objective?: string | null
    summary?: string | null
    description?: string | null
  },
  // Admin
  adminPreBuiltTasks: [] as any[],
  adminPreBuiltTasksStatus: 'idle' as 'idle' | 'loading' | 'succeeded' | 'failed',
  adminPreBuiltTaskAiStatus: 'idle' as 'idle' | 'loading' | 'succeeded' | 'failed',
  adminPreBuiltTaskAiError: null as any,
  // Tool Access: cross-chat tool invocation ledger
  toolAccess: {
    invocations: [] as any[],
    summary: null as null | {
      from: string
      to: string
      totalCredits: number
      toolCredits: number
      modelCredits: number
      scheduledCredits: number
      invocationCount: number
      billableInvocationCount: number
    },
    rangeKey: '30d' as '7d' | '30d' | 'mtd' | 'all',
    status: 'idle' as 'idle' | 'loading' | 'succeeded' | 'failed',
    error: null as any,
  },
}

export const fetchOpenAIAccount = createAsyncThunk('openai/fetchOpenAIAccount', async (payload, { rejectWithValue }) => {
  try {
    const { key_id } = payload
    let res = await axios.get(`${openAIUrl}/account/${key_id}`, getAxiosDefaultConfig())
    return res?.data
  } catch (err) {
    return rejectWithValue(err?.response?.data || err)
  }
})

export const createOpenAIAccount = createAsyncThunk('openai/createOpenAIAccount', async (payload, { rejectWithValue }) => {
  try {
    let res = await axios.post(`${openAIUrl}/account`, payload, getAxiosDefaultConfig())
    return res?.data
  } catch (err) {
    return rejectWithValue(err?.response?.data || err)
  }
})

export const updateOpenAIAccount = createAsyncThunk('openai/updateOpenAIAccount', async (payload, { rejectWithValue }) => {
  try {
    const { key_id } = payload
    let res = await axios.put(`${openAIUrl}/account/${key_id}`, payload, getAxiosDefaultConfig())
    return res?.data
  } catch (err) {
    return rejectWithValue(err?.response?.data || err)
  }
})

export const deleteOpenAIAccount = createAsyncThunk('openai/deleteOpenAIAccount', async (payload, { rejectWithValue }) => {
  try {
    const { key_id } = payload
    let res = await axios.delete(`${openAIUrl}/account/${key_id}`, getAxiosDefaultConfig())
    return res?.data
  } catch (err) {
    return rejectWithValue(err?.response?.data || err)
  }
})

export const fetchCustomTools = createAsyncThunk('openai/fetchCustomTools', async (_, { rejectWithValue }) => {
  try {
    const res = await axios.get(`${openAIUrl}/custom-tools`, getAxiosDefaultConfig())
    return res?.data?.tools || []
  } catch (err: any) {
    return rejectWithValue(err?.response?.data || err)
  }
})

export const createCustomTool = createAsyncThunk('openai/createCustomTool', async (payload: Record<string, unknown>, { rejectWithValue }) => {
  try {
    const res = await axios.post(`${openAIUrl}/custom-tools`, payload, getAxiosDefaultConfig())
    return res?.data?.tool
  } catch (err: any) {
    return rejectWithValue(err?.response?.data || err)
  }
})

export const updateCustomTool = createAsyncThunk(
  'openai/updateCustomTool',
  async (payload: { toolId: string; body: Record<string, unknown> }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${openAIUrl}/custom-tools/${payload.toolId}`, payload.body, getAxiosDefaultConfig())
      return res?.data?.tool
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  },
)

export const deleteCustomTool = createAsyncThunk('openai/deleteCustomTool', async (toolId: string, { rejectWithValue }) => {
  try {
    await axios.delete(`${openAIUrl}/custom-tools/${toolId}`, getAxiosDefaultConfig())
    return toolId
  } catch (err: any) {
    return rejectWithValue(err?.response?.data || err)
  }
})

export const testCustomTool = createAsyncThunk(
  'openai/testCustomTool',
  async (payload: { toolId: string; args?: Record<string, unknown> }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${openAIUrl}/custom-tools/${payload.toolId}/test`,
        { args: payload.args || {} },
        getAxiosDefaultConfig(),
      )
      return res?.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  },
)

export const parseOpenApiSpec = createAsyncThunk(
  'openai/parseOpenApiSpec',
  async (body: { specUrl?: string; specText?: string; baseUrl?: string }, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${openAIUrl}/custom-tools/import-spec`, body, getAxiosDefaultConfig())
      return res?.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  },
)

export const confirmOpenApiImport = createAsyncThunk(
  'openai/confirmOpenApiImport',
  async (body: { tools: any[] }, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${openAIUrl}/custom-tools/import-spec/confirm`, body, getAxiosDefaultConfig())
      return res?.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  },
)

// Backend returns HTTP 402 { code: 'insufficient_ai_credits', error } from the AI
// chat endpoints when the owner's AI credit balance is exhausted. Because the chat
// requests use responseType:'text', a pre-handler 402 surfaces in the outer catch as
// an axios error whose response.data is a raw JSON string — not the SSE stream.
export const INSUFFICIENT_AI_CREDITS_CODE = 'insufficient_ai_credits'

/** Human message + markdown CTA (rendered by ReactMarkdown / AgentResponseRenderer). */
export const insufficientAiCreditsMessage = (backendMsg?: string): string => {
  const base = (typeof backendMsg === 'string' && backendMsg.trim())
    ? backendMsg.trim()
    : "You're out of AI credits. Add credits or upgrade your plan to keep using PayHelm AI features."
  return `${base}\n\n[Buy credits](/plan?view=ai-credits) · [Enable auto-reload](/plan?view=ai-credits) · [View AI add-on](/plan?view=add-ons)`
}

/**
 * Detects the insufficient-credits 402 from an axios error and returns a structured
 * reject payload, or null if it's a different error. Tolerates the raw-string body
 * that responseType:'text' produces.
 */
export const parseInsufficientCreditsError = (err: any): { code: string; error?: string; insufficientCredits: true } | null => {
  if (err?.response?.status !== 402) return null
  let body: any = err.response.data
  if (typeof body === 'string') {
    try { body = JSON.parse(body) } catch { body = {} }
  }
  return {
    code: INSUFFICIENT_AI_CREDITS_CODE,
    error: typeof body?.error === 'string' ? body.error : undefined,
    insufficientCredits: true,
  }
}

export const callOpenAIChat = createAsyncThunk('openai/callOpenAIChat', async (payload, thunkAPI) => {
  const {
    openAIChatPayload,
    callbackOnUpdateMessage,
    callbackOnError,
    cancelToken,
  } = payload
  let toRefreshCredits = true
  const streamState = createXHRStreamState()
  let fullMessage = ''
  let fullMessageFetched = false
  let additionalContentFetched = false
  let messageId = null
  let messageStatus = ''
  let additionalContent = {}
  let requestError = null
  let csvChunk = null
  let csvProgress = null

  const processChatStreamPayloads = (payloads: string[]) => {
    if (payloads.length === 0) return
    for (const data of payloads) {
      if (fullMessageFetched && toRefreshCredits) {
        refreshCreditsAfterChat(thunkAPI.dispatch)
        toRefreshCredits = false
      }
      if (['[DONE]', '{"content":"[DONE]"}'].includes(data)) {
        fullMessageFetched = true
        callbackOnUpdateMessage({
          messageId,
          messageStatus: additionalContentFetched ? null : 'loading',
          content: fullMessage,
          ...additionalContent,
        })
        if (additionalContentFetched) break
        continue
      }
      try {
        const parsedData = JSON.parse(data)
        if (parsedData?.error) {
          requestError = parsedData?.error
        }
        else if (parsedData?.messageId) {
          messageId = parsedData?.messageId
        }
        else if (parsedData?.message_status) {
          messageStatus = parsedData?.message_status
        }
        else if (parsedData?.csvChunk !== undefined) {
          csvChunk = parsedData.csvChunk
          csvProgress = parsedData.progress
          messageStatus = parsedData.messageStatus || 'streaming'
          fullMessage = parsedData.content || ''
          fullMessageFetched = messageStatus === null
        }
        else if (parsedData?.additional_content) {
          additionalContent = _.pick(parsedData?.additional_content, ['chart', 'facet', 'overviewData', 'messageId', 'startDate', 'endDate', 'reportLink', 'customReportDbConfig', 'solrResults', 'chartConfig', 'reportConfig', 'showSuggestions', 'assistantReportConfig', 'generatedPdfs'])
          additionalContentFetched = true
          messageStatus = 'loading'
          if (fullMessageFetched) {
            callbackOnUpdateMessage({
              messageId,
              messageStatus: null,
              content: fullMessage,
              ...additionalContent,
            })
            break
          }
        }
        else {
          fullMessageFetched = (parsedData?.content || '').endsWith('[DONE]')
          messageStatus = fullMessageFetched ? null : 'loading'
          fullMessage += (parsedData?.content || '').replace(/\[DONE\]$/, '')
        }
      }
      catch (err) {
        console.error(err)
      }
    }
    if (requestError) {
      callbackOnError(requestError)
      return
    }
    if (!fullMessageFetched) {
      callbackOnUpdateMessage({
        messageId,
        messageStatus,
        content: fullMessage,
        csvChunk: csvChunk,
        progress: csvProgress,
        ...additionalContent,
      })
    }
  }

  try {
    await axios.post(
      `${openAIUrl}/chat`,
      openAIChatPayload,
      {
        responseType: 'text',
        adapter: 'xhr', // Force XHR adapter to enable onDownloadProgress with response text
        cancelToken: cancelToken?.token,
        onDownloadProgress: (progressEvent) => {
          const payloads = feedXHRStreamProgress(streamState, getXHRResponseText(progressEvent))
          processChatStreamPayloads(payloads)
        },
        ...getAxiosDefaultConfig()
      }
    )
    const remainingPayloads = feedXHRStreamProgress(streamState, streamState.lastText, true)
    processChatStreamPayloads(remainingPayloads)
    if (fullMessageFetched && toRefreshCredits) {
      refreshCreditsAfterChat(thunkAPI.dispatch)
      toRefreshCredits = false
    }
  } catch (err) {
    // Check if the request was cancelled by the user
    if (axios.isCancel(err)) {
      callbackOnError('Chat request was cancelled.')
      return thunkAPI.rejectWithValue({ cancelled: true, message: 'Request cancelled by user' })
    }

    // Out of AI credits (402): show the actionable message + upgrade CTA, not a generic error.
    const creditErr = parseInsufficientCreditsError(err)
    if (creditErr) {
      callbackOnError(insufficientAiCreditsMessage(creditErr.error))
      await thunkAPI.dispatch(getUserCredits())
      return thunkAPI.rejectWithValue(creditErr)
    }

    console.error('[OPENAI CHAT ERROR]', err?.response?.data || err?.message || err)
    callbackOnError('I ran into an error.  Our team is reviewing it so we can continually improve.')
    await thunkAPI.dispatch(getUserCredits())
    return thunkAPI.rejectWithValue(err?.response?.data || err)
  }
})

export const rateOpenAIChat = createAsyncThunk('openai/rateOpenAIChat', async (payload, thunkAPI) => {
  try {
    let res = await axios.post(`${openAIUrl}/chat/rate`, payload, getAxiosDefaultConfig())
    return res?.data
  } catch (err) {
    return thunkAPI.rejectWithValue(err?.response?.data || err)
  }
})

const HISTORY_PAGE_SIZE = 20

/** Normalise history API response — handles both plain array (legacy) and { items, hasMore } (paginated).
 *
 * The fetchOpenAIChatHistory thunk always sends ?limit=N&offset=M, so the backend will
 * always return the paginated { items, hasMore } shape in practice. The plain-array path
 * is a safety net for dev/staging environments running an older server binary.
 * When a plain array IS returned we cap it at HISTORY_PAGE_SIZE to avoid rendering
 * thousands of rows at once; hasMore is false so no "Load more" button appears, which
 * is the honest UX (we don't have a server-side cursor to continue from).
 */
function normaliseHistoryResponse(data: any): { items: any[]; hasMore: boolean } {
  if (Array.isArray(data)) {
    return { items: data.slice(0, HISTORY_PAGE_SIZE), hasMore: false }
  }
  return { items: Array.isArray(data?.items) ? data.items : [], hasMore: Boolean(data?.hasMore) }
}

/** Initial page load — replaces existing history. */
const CHAT_HISTORY_BUBBLE_QS = 'includeBubbleHistory=1'

export const fetchOpenAIChatHistory = createAsyncThunk('openai/fetchOpenAIChatHistory', async (payload: any, { rejectWithValue }) => {
  try {
    const { app_email } = payload
    const res = await axios.get(
      `${openAIUrl}/chat/history/${app_email}?limit=${HISTORY_PAGE_SIZE}&offset=0&${CHAT_HISTORY_BUBBLE_QS}`,
      getAxiosDefaultConfig()
    )
    return normaliseHistoryResponse(res?.data)
  } catch (err: any) {
    return rejectWithValue(err?.response?.data || err)
  }
})

/** Load the next page — appends to existing history. */
export const fetchMoreOpenAIChatHistory = createAsyncThunk('openai/fetchMoreOpenAIChatHistory', async (payload: any, { rejectWithValue }) => {
  try {
    const { app_email, offset } = payload
    const res = await axios.get(
      `${openAIUrl}/chat/history/${app_email}?limit=${HISTORY_PAGE_SIZE}&offset=${offset}&${CHAT_HISTORY_BUBBLE_QS}`,
      getAxiosDefaultConfig()
    )
    return normaliseHistoryResponse(res?.data)
  } catch (err: any) {
    return rejectWithValue(err?.response?.data || err)
  }
})

/** Fetch a single conversation, optionally paginating its turns (for long conversations). */
export const fetchOpenAIChatConversation = createAsyncThunk('openai/fetchOpenAIChatConversation', async (payload: any, { rejectWithValue }) => {
  try {
    const { app_email, message_id, turnLimit, turnOffset } = payload
    const params = turnLimit != null
      ? (turnOffset != null ? `?turnLimit=${turnLimit}&turnOffset=${turnOffset}` : `?turnLimit=${turnLimit}`)
      : ''
    const res = await axios.get(`${openAIUrl}/chat/history/${app_email}/${message_id}${params}`, getAxiosDefaultConfig())
    return res?.data
  } catch (err: any) {
    return rejectWithValue(err?.response?.data || err)
  }
})

export const deleteOpenAIChatHistory = createAsyncThunk('openai/deleteOpenAIChatHistory', async (payload, thunkAPI) => {
  try {
    const { message_id, app_email } = payload
    let res = await axios.delete(`${openAIUrl}/chat/history/${app_email}/${message_id}`, getAxiosDefaultConfig())
    // Refetch the chat history after deletion
    await thunkAPI.dispatch(fetchOpenAIChatHistory({ app_email }))
    return res?.data
  } catch (err) {
    return thunkAPI.rejectWithValue(err?.response?.data || err)
  }
})

export const deleteAllOpenAIChatHistory = createAsyncThunk('openai/deleteAllOpenAIChatHistory', async (payload, thunkAPI) => {
  try {
    const { app_email } = payload
    let res = await axios.delete(`${openAIUrl}/chat/history/all/${app_email}`, getAxiosDefaultConfig())
    // Refetch the chat history after deletion
    await thunkAPI.dispatch(fetchOpenAIChatHistory({ app_email }))
    return res?.data
  } catch (err) {
    return thunkAPI.rejectWithValue(err?.response?.data || err)
  }
})

export const updateOpenAIChatPrivacy = createAsyncThunk('openai/updateOpenAIChatPrivacy', async (payload, thunkAPI) => {
  try {
    const { message_id, app_email, is_private } = payload
    let res = await axios.put(`${openAIUrl}/chat/history/${app_email}/${message_id}/privacy`, { is_private }, getAxiosDefaultConfig())
    // Refetch the chat history after update
    await thunkAPI.dispatch(fetchOpenAIChatHistory({ app_email }))
    return res?.data
  } catch (err) {
    return thunkAPI.rejectWithValue(err?.response?.data || err)
  }
})

/**
 * Load the model/effort catalog. Failure is non-fatal: the bundled fallback
 * stays in place, so the pickers keep working offline or against an older API.
 */
export const fetchAgentModelCatalog = createAsyncThunk(
  'openai/fetchAgentModelCatalog',
  async (_arg, thunkAPI) => {
    try {
      const res = await axios.get(`${openAIUrl}/chat/agent/models`, getAxiosDefaultConfig())
      return normalizeAgentModelCatalog(res?.data)
    } catch (err) {
      return thunkAPI.rejectWithValue(err?.response?.data || err)
    }
  },
  {
    // Every chat surface dispatches this on mount (the AI page and each
    // AIChatBox), so drop a request while one is already in flight. Not gated
    // on 'succeeded': switching accounts should still refetch.
    condition: (_arg, { getState }) =>
      (getState() as any)?.openai?.agentCatalog?.status !== 'loading',
  }
)

export const togglePinOpenAIChatHistory = createAsyncThunk('openai/togglePinChatHistory', async (payload: { message_id: string; app_email: string; pinned: boolean }, thunkAPI) => {
  try {
    const { message_id, app_email, pinned } = payload
    let res = await axios.put(`${openAIUrl}/chat/history/${app_email}/${message_id}/pin`, { pinned }, getAxiosDefaultConfig())
    await thunkAPI.dispatch(fetchOpenAIChatHistory({ app_email }))
    return res?.data
  } catch (err) {
    return thunkAPI.rejectWithValue(err?.response?.data || err)
  }
})

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))
export const fetchRelatedQuestions = createAsyncThunk(
  'openai/fetchRelatedQuestions',
  async (payload, { rejectWithValue }) => {
    try {
      const { sourcePage, isMimic } = payload
      const res = await axios.post(
        `${openAIUrl}/chat/insights-questions`,
        {
          sourcePage,
          isMimic,
        },
        getAxiosDefaultConfig()
      )
      return res?.data
    } catch (err) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const askForInsight = createAsyncThunk(
  'openai/askForInsight',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { question, data, dataTypes, sourcePage, key_id, isMimic, messageId, model } = payload
      const streamState = createXHRStreamState()
      let fullMessage = ''
      let fullMessageFetched = false
      let additionalContent = {}
      let csvChunk = null
      let csvProgress = null

      const processInsightStreamPayloads = (payloads: string[]) => {
        if (payloads.length === 0) return
        for (const data of payloads) {
          if (data === '[DONE]') {
            fullMessageFetched = true
            continue
          }

          if (data === '[UNRELATED]') {
            fullMessage = 'Sorry, I cannot find related insights from this report.'
            fullMessageFetched = true
            break
          }

          try {
            const parsedData = JSON.parse(data)

            if (parsedData.messageId) {
              dispatch(setMessageId(parsedData.messageId))
            }

            if (parsedData.csvChunk !== undefined) {
              csvChunk = parsedData.csvChunk
              csvProgress = parsedData.progress
              fullMessage += parsedData.content || ''
              fullMessageFetched = parsedData.messageStatus === null
            }
            else if (parsedData.additional_content) {
              additionalContent = _.pick(parsedData.additional_content, [
                'chart', 'facet', 'overviewData', 'messageId', 'startDate', 'endDate',
                'reportLink', 'customReportDbConfig', 'solrResults', 'chartConfig',
                'reportConfig', 'showSuggestions', 'assistantReportConfig',
                // Backend re-emits the SDK session id here as a fallback for a
                // missed init frame. Without it in the pick list that fallback is
                // silently dropped and the next turn replays the transcript.
                'sessionId',
                // #935: per-prompt cost breakdown (model vs tools) rendered
                // under the assistant message.
                'creditSummary',
              ])

              if (fullMessageFetched) {
                dispatch(updateInsightChunk({
                  sourcePage,
                  data: fullMessage,
                  csvChunk,
                  csvProgress,
                  ...additionalContent
                }))
                break
              }
            }
            else if (parsedData.content) {
              fullMessageFetched = parsedData.content.endsWith('[DONE]')
              fullMessage += parsedData.content.replace(/\[DONE\]$/, '')
            }
          } catch (parseErr) {
            console.error('[askForInsight] Parse error:', parseErr)
          }
        }

        dispatch(updateInsightChunk({
          sourcePage,
          data: fullMessage,
          csvChunk,
          csvProgress,
          ...additionalContent
        }))
      }

      await axios.post(
        `${openAIUrl}/chat/insights-route`,
        {
          userMessage: question,
          question,
          data,
          dataTypes,
          sourcePage,
          key_id,
          isMimic,
          message_id: messageId,
          model: model || 'gpt-3.5-turbo-1106',
        },
        {
          responseType: 'text',
          adapter: 'xhr', // Force XHR adapter to enable onDownloadProgress with response text
          onDownloadProgress: (progressEvent) => {
            const payloads = feedXHRStreamProgress(streamState, getXHRResponseText(progressEvent))
            processInsightStreamPayloads(payloads)
          },
          ...getAxiosDefaultConfig()
        }
      )

      const remainingPayloads = feedXHRStreamProgress(streamState, streamState.lastText, true)
      processInsightStreamPayloads(remainingPayloads)

      await sleep(500)
    } catch (err) {
      console.error('[askForInsight] Error:', err)
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const updateInsightChunk = createAsyncThunk(
  'openai/updateInsightChunk',
  (payload) => {
    return payload || {}
  }
)

/** Detect PayHelm MCP scheduled_tasks mutations in streamed tool output (title may appear without tool name on some SDK paths). */
function toolOutputIndicatesScheduledTaskMutation(toolOutput: unknown): boolean {
  if (typeof toolOutput !== 'string') return false
  return (
    toolOutput.includes('Scheduled task created')
    || toolOutput.includes('Scheduled task updated')
    || toolOutput.includes('Scheduled task deleted')
  )
}

/**
 * Parse MCP start_backfill toolOutput and seed sidebar polling when a job started
 * (or is already running). Returns true when the payload was handled (including
 * dry_run / plain errors that should not trigger discovery).
 */
function tryWatchBackfillFromToolOutput(toolOutput: unknown, dispatch: (action: any) => void): boolean {
  if (typeof toolOutput !== 'string') return false
  try {
    const parsed = JSON.parse(toolOutput)
    const result = Array.isArray(parsed?.results) ? parsed.results[0] : null
    const meta = result?.metadata ?? parsed?.data ?? (parsed?.backfill_id || parsed?.response ? parsed : null)
    if (!meta || typeof meta !== 'object') return false

    if (meta.dry_run) return true

    const row =
      (meta.response && meta.response.id ? meta.response : null)
      || (meta.backfill && meta.backfill.id ? meta.backfill : null)
      || (meta.backfill_id
        ? {
            id: meta.backfill_id,
            status: meta.status || 'running',
            merchant_id: meta.merchant_id || meta.account_id,
            current: meta.current,
            total: meta.total,
            created_at: meta.created_at,
            status_message: meta.status_message,
          }
        : null)

    if (row?.id && (meta.response || meta.backfill || meta.duplicate_running_backfill || meta.backfill_id)) {
      // Plain failures include metadata.error without a started/running job.
      if (meta.error && !meta.duplicate_running_backfill && !meta.response && !meta.backfill) {
        return true
      }
      dispatch(watchBackfill(row))
      return true
    }

    // Explicit error result with no backfill id — don't rediscover.
    if (result?.id === 'error' || meta.error) return true

    return false
  } catch {
    return false
  }
}

/** Backup when SSE omits tool name — match start_backfill success / already-running copy. */
function toolOutputIndicatesStartedBackfill(toolOutput: unknown): boolean {
  if (typeof toolOutput !== 'string') return false
  return (
    toolOutput.includes('duplicate_running_backfill')
    || /Started [\w\s]+ backfill/.test(toolOutput)
  )
}

/** When My AI finishes start_backfill, show the sidebar card without requiring a refresh. */
function maybeWatchBackfillFromAgentStep(
  tool: unknown,
  toolOutput: unknown,
  dispatch: (action: any) => void,
): void {
  const toolStr = typeof tool === 'string' ? tool : ''
  if (!toolStr.includes('start_backfill') && !toolOutputIndicatesStartedBackfill(toolOutput)) return
  if (!tryWatchBackfillFromToolOutput(toolOutput, dispatch)) {
    void dispatch(discoverRunningBackfills() as any)
  }
}

// Agent mode thunk - handles agentic chat with up to 20 iterations
export const askAgentForInsight = createAsyncThunk(
  'openai/askAgentForInsight',
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      const {
        question,
        data,
        dataTypes,
        sourcePage,
        // Persisted openai_chat_history.source_page. Chat 2.0 keeps `sourcePage` as the
        // per-chat Redux stream key (often messageId) so concurrent chats do not mix;
        // history listing only accepts named sources like `ai`, so callers that use a
        // message-id bucket must pass historySourcePage separately.
        historySourcePage,
        key_id,
        isMimic,
        messageId,
        model,
        imageModel,
        conversationHistory,
        signal,
        effortLevel,
        images,
        csvAttachments,
        agentAttachments,
        preBuiltTask,
      } = payload
      const persistedSourcePage = historySourcePage || sourcePage

      // Resume token for this conversation, captured from a previous turn's
      // stream. Read before dispatching anything so it reflects the state at
      // request time.
      const resumeSessionId = messageId
        ? (getState() as any)?.openai?.agentSessionIds?.[messageId]
        : undefined

      // Always clear agent steps on new request — prevents stale plan/steps from previous run
      dispatch(clearAgentSteps({ sourcePage }))
      dispatch(clearKnowledgeSources({ sourcePage }))
      dispatch(clearGeneratedPdfs({ sourcePage }))

      dispatch(setAgentStatus({ sourcePage, status: 'running' }))

      // Track errors received from the SSE stream so we can reject the thunk
      let streamError: string | null = null
      let agentDidFail = false

      // Sanitize curated workflow context: forward only the task name + objective
      // that the backend needs for the first-response kickoff.
      const sanitizedPreBuiltTask = preBuiltTask && typeof preBuiltTask === 'object'
        ? {
            id: preBuiltTask.id,
            name: preBuiltTask.name,
            objective: preBuiltTask.objective ?? null,
            summary: preBuiltTask.summary ?? null,
            description: preBuiltTask.description ?? null,
          }
        : null

      const streamState = createXHRStreamState()
      let fullMessage = ''
      let fullMessageFetched = false
      let additionalContent = {}

      const processAgentStreamPayloads = (payloads: string[]) => {
        if (payloads.length === 0) return
        for (const dataStr of payloads) {
          if (dataStr === '[DONE]') {
            fullMessageFetched = true
            continue
          }

          try {
            const parsedData = JSON.parse(dataStr)

            if (parsedData.error) {
              console.error('[askAgentForInsight] Server error:', parsedData.error)
              streamError = parsedData.error
            }

            if (parsedData.messageId) {
              dispatch(setMessageId(parsedData.messageId))
            }

            // The backend emits the SDK session id early in the stream (and
            // again in additional_content). Remember it so the next turn in
            // this chat resumes instead of replaying the transcript.
            if (parsedData.sessionId && messageId) {
              dispatch(setAgentSessionId({ chatId: messageId, sessionId: parsedData.sessionId }))
            }

            if (parsedData.agent_step) {
              const step = parsedData.agent_step
              if (step.type === 'new') {
                dispatch(addAgentStep({
                  sourcePage,
                  step: {
                    iteration: step.iteration,
                    status: step.status || 'running',
                    thought: step.thought || '',
                    tool: step.tool || null,
                    toolInput: step.toolInput || null,
                    toolOutput: step.toolOutput || null,
                    toolUseId: step.toolUseId || null,
                    timestamp: Date.now(),
                  }
                }))
              } else if (step.type === 'update') {
                dispatch(updateAgentStep({
                  sourcePage,
                  iteration: step.iteration,
                  tool: step.tool,
                  toolUseId: step.toolUseId,
                  updates: {
                    status: step.status,
                    thought: step.thought,
                    toolInput: step.toolInput,
                    toolOutput: step.toolOutput,
                  }
                }))
                if (step.status === 'completed') {
                  const toolStr = typeof step.tool === 'string' ? step.tool : ''
                  const fromTool = toolStr.includes('manage_scheduled_tasks')
                  const fromOutput = toolOutputIndicatesScheduledTaskMutation(step.toolOutput)
                  if (fromTool || fromOutput) {
                    void dispatch(fetchScheduledTasks() as any)
                  }
                  maybeWatchBackfillFromAgentStep(step.tool, step.toolOutput, dispatch)
                }
              }
            }

            if (parsedData.tool_insight) {
              const insight = parsedData.tool_insight
              if (insight.toolUseId && insight.text) {
                dispatch(updateAgentStep({
                  sourcePage,
                  iteration: undefined,
                  tool: undefined,
                  toolUseId: insight.toolUseId,
                  updates: { insight: insight.text },
                }))
              }
            }

            if (parsedData.narration?.text) {
              dispatch(addAgentNarration({
                sourcePage,
                narration: {
                  text: parsedData.narration.text,
                  atStep: parsedData.narration.atStep ?? 0,
                  workedForMs: parsedData.narration.workedForMs ?? 0,
                  timestamp: Date.now(),
                },
              }))
            }

            if (parsedData.additional_content) {
              additionalContent = _.pick(parsedData.additional_content, [
                'chart', 'facet', 'overviewData', 'messageId', 'startDate', 'endDate',
                'reportLink', 'customReportDbConfig', 'solrResults', 'chartConfig',
                'reportConfig', 'showSuggestions', 'assistantReportConfig',
                'generatedPdfs',
                // Backend re-emits the SDK session id here as a fallback for a
                // missed init frame. Without it in the pick list that fallback is
                // silently dropped and the next turn replays the transcript.
                'sessionId',
                // #935: per-prompt cost breakdown (model vs tools) rendered
                // under the assistant message.
                'creditSummary',
              ])

              if (Array.isArray(additionalContent.generatedPdfs) && additionalContent.generatedPdfs.length > 0) {
                dispatch(setGeneratedPdfs({ sourcePage, pdfs: additionalContent.generatedPdfs }))
              }

              // Actually honour the fallback. Keyed on the same client-side chat
              // id the init frame uses, so whichever arrives first wins and the
              // next turn resumes either way.
              if (additionalContent.sessionId && messageId) {
                dispatch(setAgentSessionId({ chatId: messageId, sessionId: additionalContent.sessionId }))
              }

              // #935: park the cost breakdown in state; the chat entry is
              // assembled from explicit fields, so it cannot ride along on
              // additionalContent.
              dispatch(setAgentCreditSummary({
                sourcePage,
                creditSummary: additionalContent.creditSummary || null,
              }))

              if (fullMessageFetched) {
                dispatch(updateInsightChunk({
                  sourcePage,
                  data: fullMessage,
                  ...additionalContent
                }))
                break
              }
            }

            if (parsedData.content) {
              fullMessageFetched = parsedData.content.endsWith('[DONE]')
              fullMessage += parsedData.content.replace(/\[DONE\]$/, '')
            }

            if (parsedData.agent_status) {
              dispatch(setAgentStatus({ sourcePage, status: parsedData.agent_status }))
              if (parsedData.agent_status === 'failed') {
                agentDidFail = true
              }
            }

            if (parsedData.memory_update) {
              dispatch(setAgentMemoryUpdate({
                sourcePage,
                memoryUpdate: parsedData.memory_update
              }))
            }

            if (parsedData.available_tools && Array.isArray(parsedData.available_tools)) {
              dispatch(setAgentAvailableTools({
                sourcePage,
                tools: parsedData.available_tools
              }))
            }

            if (parsedData.knowledge_sources && Array.isArray(parsedData.knowledge_sources)) {
              dispatch(setKnowledgeSources({
                sourcePage,
                sources: parsedData.knowledge_sources,
              }))
            }

            if (parsedData.csuite_review_start) {
              dispatch(setCsuiteReviewStatus({ sourcePage, status: 'reviewing' }))
            }

            if (parsedData.csuite_review_step) {
              const cstep = parsedData.csuite_review_step
              dispatch(addCsuiteReviewStep({
                sourcePage,
                step: {
                  role: cstep.role,
                  title: cstep.title,
                  icon: cstep.icon,
                  color: cstep.color,
                  type: cstep.type,
                  review: cstep.review || null,
                  request: cstep.request || null,
                  timestamp: cstep.timestamp,
                }
              }))
              if (cstep.type === 'reviewing') {
                dispatch(addAgentStep({
                  sourcePage,
                  step: {
                    iteration: -1,
                    status: 'running',
                    thought: `csuite:reviewing:${cstep.role}:${cstep.title}`,
                    tool: null,
                    toolInput: null,
                    toolOutput: null,
                    toolUseId: `csuite-${cstep.role}`,
                    timestamp: Date.now(),
                  }
                }))
              } else if (cstep.type === 'completed') {
                dispatch(updateAgentStep({
                  sourcePage,
                  iteration: -1,
                  tool: undefined,
                  toolUseId: `csuite-${cstep.role}`,
                  updates: {
                    status: 'completed',
                    thought: `csuite:completed:${cstep.role}:${cstep.title}:${cstep.review || ''}`,
                  }
                }))
              }
            }

            if (parsedData.csuite_review_complete) {
              dispatch(setCsuiteReviewStatus({ sourcePage, status: 'completed' }))
            }

            if (parsedData.csuite_rerun) {
              // C-suite re-run triggered — handled by agent handler
            }

          } catch (parseErr) {
            console.error('[askAgentForInsight] Parse error:', parseErr)
          }
        }

        dispatch(updateInsightChunk({
          sourcePage,
          data: fullMessage,
          ...additionalContent
        }))
      }

      await axios.post(
        `${openAIUrl}/chat/agent`,
        {
          userMessage: question,
          question,
          data,
          dataTypes,
          sourcePage: persistedSourcePage,
          key_id,
          isMimic,
          message_id: messageId,
          model: model || 'sonnet',
          ...(imageModel ? { imageModel } : {}),
          agentMode: true,
          // Effort level (1-5) replaces autonomyLevel + maxIterations; the backend
          // derives maxTurns/effort/model-floor from it.
          // Omitted when unknown: papaya's DEFAULT_EFFORT_LEVEL cannot go stale,
          // a client constant can.
          ...(effortLevel != null ? { effortLevel } : {}),
          conversationHistory: conversationHistory || [],
          // Resume the SDK session for this chat when we have one. The backend
          // falls back to replaying conversationHistory if the id is stale, so
          // sending it is never worse than omitting it.
          ...(resumeSessionId ? { sessionId: resumeSessionId } : {}),
          clientTimezone: detectBrowserTimezone(),
          clientTimezoneOffsetMinutes: detectBrowserTimezoneOffsetMinutes(),
          ...(images?.length ? { images } : {}),
          ...(csvAttachments?.length ? { csvAttachments } : {}),
          ...(agentAttachments?.length ? { agentAttachments } : {}),
          ...(sanitizedPreBuiltTask ? { preBuiltTask: sanitizedPreBuiltTask } : {}),
        },
        {
          responseType: 'text',
          adapter: 'xhr',
          onDownloadProgress: (progressEvent) => {
            const payloads = feedXHRStreamProgress(streamState, getXHRResponseText(progressEvent))
            processAgentStreamPayloads(payloads)
          },
          signal, // AbortController signal for cancellation
          ...getAxiosDefaultConfig()
        }
      )

      const remainingPayloads = feedXHRStreamProgress(streamState, streamState.lastText, true)
      processAgentStreamPayloads(remainingPayloads)

      // If the stream reported an error, reject the thunk so the UI shows the error
      if (streamError) {
        dispatch(setAgentStatus({ sourcePage, status: 'failed' }))
        return rejectWithValue({ error: streamError })
      }

      await sleep(500)
      // Only set completed if the agent didn't already report failure
      if (!agentDidFail) {
        dispatch(setAgentStatus({ sourcePage, status: 'completed' }))
        // Backup refresh: SSE tool_result may omit `tool` or deduped steps may miss updates — scan final agent steps
        const steps = (getState() as { openai?: { agentSteps?: Record<string, any[]> } })?.openai?.agentSteps?.[sourcePage] ?? []
        const shouldRefetch = steps.some(
          (s: { status?: string; toolOutput?: unknown }) =>
            s?.status === 'completed' && toolOutputIndicatesScheduledTaskMutation(s?.toolOutput),
        )
        if (shouldRefetch) {
          void dispatch(fetchScheduledTasks() as any)
        }
        steps.forEach((s: { status?: string; tool?: unknown; toolOutput?: unknown }) => {
          if (s?.status === 'completed') {
            maybeWatchBackfillFromAgentStep(s.tool, s.toolOutput, dispatch)
          }
        })
      }

      // Play BC Ferries horn sound on completion 🚢 — only when tab is not focused
      // Modeled after the Spirit-class two-tone chord horns heard on the Victoria–Vancouver route
      if (document.hidden) try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
        const t = ctx.currentTime
        const duration = 2.1

        // Master output with compression to prevent clipping
        const compressor = ctx.createDynamicsCompressor()
        compressor.threshold.setValueAtTime(-12, t)
        compressor.ratio.setValueAtTime(6, t)
        compressor.connect(ctx.destination)

        const masterGain = ctx.createGain()
        masterGain.gain.setValueAtTime(0.45, t)
        masterGain.connect(compressor)

        // BC Ferries use a two-tone chord horn — low A2 + C#3 (a major third)
        const lowTone = 110    // A2
        const highTone = 138.6 // C#3

        // Helper: create a horn voice with pressure-buildup envelope + pitch rise
        const voice = (freq: number, vol: number, type: OscillatorType, detune: number) => {
          const osc = ctx.createOscillator()
          const gain = ctx.createGain()
          osc.type = type
          // Pitch rises slightly as horn pressurizes, then settles
          osc.frequency.setValueAtTime(freq * 0.97, t)
          osc.frequency.linearRampToValueAtTime(freq, t + 0.4)
          osc.detune.setValueAtTime(detune, t)
          // Envelope: air rush → bloom → sustain → fade
          gain.gain.setValueAtTime(0, t)
          gain.gain.linearRampToValueAtTime(vol * 0.3, t + 0.08)  // Initial air burst
          gain.gain.linearRampToValueAtTime(vol, t + 0.5)          // Full bloom
          gain.gain.setValueAtTime(vol, t + duration - 0.8)        // Sustain
          gain.gain.linearRampToValueAtTime(0, t + duration)        // Fade
          osc.connect(gain)
          gain.connect(masterGain)
          osc.start(t)
          osc.stop(t + duration + 0.1)
        }

        // --- Low tone (A2) voices ---
        voice(lowTone, 0.35, 'sine', 0)          // Core fundamental
        voice(lowTone, 0.18, 'sine', 7)           // Detuned for beating/vibration
        voice(lowTone, 0.10, 'triangle', -4)       // Brassy edge
        voice(lowTone * 2, 0.08, 'sine', 0)       // Octave harmonic

        // --- High tone (C#3) voices ---
        voice(highTone, 0.28, 'sine', 0)          // Core fundamental
        voice(highTone, 0.14, 'sine', -6)          // Detuned beating
        voice(highTone, 0.07, 'triangle', 5)       // Brassy edge
        voice(highTone * 2, 0.05, 'sine', 0)      // Octave harmonic

        // --- Air noise (the initial rush of compressed air) ---
        const bufferSize = ctx.sampleRate * duration
        const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
        const noiseData = noiseBuffer.getChannelData(0)
        for (let i = 0; i < bufferSize; i++) {
          noiseData[i] = (Math.random() * 2 - 1) * 0.5
        }
        const noise = ctx.createBufferSource()
        noise.buffer = noiseBuffer
        // Bandpass filter the noise to low frequencies (horn resonance)
        const noiseFilter = ctx.createBiquadFilter()
        noiseFilter.type = 'bandpass'
        noiseFilter.frequency.setValueAtTime(200, t)
        noiseFilter.Q.setValueAtTime(0.8, t)
        const noiseGain = ctx.createGain()
        noiseGain.gain.setValueAtTime(0, t)
        noiseGain.gain.linearRampToValueAtTime(0.06, t + 0.05)  // Quick air burst
        noiseGain.gain.linearRampToValueAtTime(0.02, t + 0.4)   // Settles down
        noiseGain.gain.setValueAtTime(0.02, t + duration - 0.8)
        noiseGain.gain.linearRampToValueAtTime(0, t + duration)
        noise.connect(noiseFilter)
        noiseFilter.connect(noiseGain)
        noiseGain.connect(masterGain)
        noise.start(t)
        noise.stop(t + duration + 0.1)

        // --- Reverb (echo off water/mountains) ---
        const reverbLen = ctx.sampleRate * 1.5
        const reverbBuffer = ctx.createBuffer(2, reverbLen, ctx.sampleRate)
        for (let ch = 0; ch < 2; ch++) {
          const data = reverbBuffer.getChannelData(ch)
          for (let i = 0; i < reverbLen; i++) {
            data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.4))
          }
        }
        const reverb = ctx.createConvolver()
        reverb.buffer = reverbBuffer
        const reverbGain = ctx.createGain()
        reverbGain.gain.setValueAtTime(0.15, t)
        masterGain.connect(reverb)
        reverb.connect(reverbGain)
        reverbGain.connect(compressor)

      } catch (e) {
        // Audio playback failed silently
      }
    } catch (err) {
      // Check if the request was cancelled by the user
      if (axios.isCancel(err) || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        dispatch(setAgentStatus({ sourcePage: payload.sourcePage, status: 'cancelled' }))
        return rejectWithValue({ cancelled: true, message: 'Request cancelled by user' })
      }
      // Out of AI credits (402): surface a structured payload so the UI can show the
      // actionable message + upgrade CTA instead of a generic failure, and refresh the balance.
      const creditErr = parseInsufficientCreditsError(err)
      if (creditErr) {
        dispatch(getUserCredits())
        dispatch(setAgentStatus({ sourcePage: payload.sourcePage, status: 'failed' }))
        return rejectWithValue(creditErr)
      }
      console.error('[askAgentForInsight] Error:', err)
      dispatch(setAgentStatus({ sourcePage: payload.sourcePage, status: 'failed' }))
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

/**
 * Chat Preferences Thunks - For seamless conversation resumption
 */
export const fetchChatPreferences = createAsyncThunk(
  'openai/fetchChatPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${openAIUrl}/chat/preferences`, getAxiosDefaultConfig())
      return res?.data
    } catch (err) {
      console.error('[fetchChatPreferences] Error:', err)
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const updateChatPreferences = createAsyncThunk(
  'openai/updateChatPreferences',
  async (payload: { lastActiveChatId?: string; autoLoadEnabled?: boolean }, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${openAIUrl}/chat/preferences`, payload, getAxiosDefaultConfig())
      return { ...res?.data, ...payload }
    } catch (err) {
      console.error('[updateChatPreferences] Error:', err)
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const fetchAgentSettings = createAsyncThunk(
  'openai/fetchAgentSettings',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(preferencesUrl, getAxiosDefaultConfig())
      agentSettingsFetchStarted = false
      return res?.data?.agentSettings
    } catch (err) {
      agentSettingsFetchStarted = false
      console.error('[fetchAgentSettings] Error:', err)
      return rejectWithValue(err?.response?.data || err)
    }
  },
  {
    condition: () => {
      if (agentSettingsFetchStarted) return false
      agentSettingsFetchStarted = true
      return true
    },
  }
)

export const saveAgentSettings = createAsyncThunk(
  'openai/saveAgentSettings',
  async (settings: AgentSettingsPayload, { getState, rejectWithValue }) => {
    try {
      const email = (getState() as any).root?.user?.email
      if (!email) return settings
      await axios.post(`${preferencesUrl}/agent_settings`, { email, settings }, getAxiosDefaultConfig())
      return settings
    } catch (err) {
      console.error('[saveAgentSettings] Error:', err)
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const markSessionInterrupted = createAsyncThunk(
  'openai/markSessionInterrupted',
  async (payload: { messageId: string; reason?: string }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${openAIUrl}/chat/mark-interrupted/${payload.messageId}`,
        { reason: payload.reason },
        getAxiosDefaultConfig()
      )
      return res?.data
    } catch (err) {
      console.error('[markSessionInterrupted] Error:', err)
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

// Scheduled tasks thunks
export const fetchScheduledTasks = createAsyncThunk(
  'openai/fetchScheduledTasks',
  async (opts: { kind?: 'recurring' | 'once'; includeCompleted?: boolean } | undefined, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams()
      if (opts?.kind) params.set('kind', opts.kind)
      // Completed one-time tasks are hidden by default (scale). Pass includeCompleted: true to load them.
      if (opts?.includeCompleted) params.set('include_completed', 'true')
      const qs = params.toString()
      const res = await axios.get(`${scheduledTasksUrl}${qs ? `?${qs}` : ''}`, getAxiosDefaultConfig())
      return res?.data?.tasks ?? []
    } catch (err) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const reorderScheduledTasks = createAsyncThunk(
  'openai/reorderScheduledTasks',
  async (orderedIds: string[], { rejectWithValue }) => {
    try {
      const res = await axios.put(
        `${scheduledTasksUrl}/reorder`,
        { ordered_ids: orderedIds },
        getAxiosDefaultConfig(),
      )
      return res?.data?.tasks ?? []
    } catch (err) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const createScheduledTask = createAsyncThunk(
  'openai/createScheduledTask',
  async (payload: Record<string, any>, { rejectWithValue }) => {
    try {
      const res = await axios.post(scheduledTasksUrl, payload, getAxiosDefaultConfig())
      return res?.data
    } catch (err) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const updateScheduledTask = createAsyncThunk(
  'openai/updateScheduledTask',
  async (payload: { id: string; [k: string]: any }, { rejectWithValue }) => {
    try {
      const { id, ...body } = payload
      const res = await axios.put(`${scheduledTasksUrl}/${id}`, body, getAxiosDefaultConfig())
      return res?.data
    } catch (err) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const deleteScheduledTask = createAsyncThunk(
  'openai/deleteScheduledTask',
  async (taskId: string, { rejectWithValue }) => {
    try {
      await axios.delete(`${scheduledTasksUrl}/${taskId}`, getAxiosDefaultConfig())
      return taskId
    } catch (err) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const toggleScheduledTask = createAsyncThunk(
  'openai/toggleScheduledTask',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${scheduledTasksUrl}/${taskId}/pause`, {}, getAxiosDefaultConfig())
      return res?.data
    } catch (err) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const runScheduledTask = createAsyncThunk(
  'openai/runScheduledTask',
  async (
    payload: string | { taskId: string; manual?: boolean; signal?: AbortSignal },
    { rejectWithValue }
  ) => {
    try {
      const taskId = typeof payload === 'string' ? payload : payload.taskId
      const body = typeof payload === 'object' && payload.manual ? { manual: true } : {}
      const signal = typeof payload === 'object' ? payload.signal : undefined
      const res = await axios.post(`${scheduledTasksUrl}/${taskId}/run`, body, {
        ...getAxiosDefaultConfig(),
        ...(signal ? { signal } : {}),
      })
      return { taskId, run: res?.data }
    } catch (err: any) {
      const canceled =
        axios.isCancel?.(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError'
      if (canceled) {
        return rejectWithValue({ cancelled: true })
      }
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const fetchTaskRuns = createAsyncThunk(
  'openai/fetchTaskRuns',
  async (payload: { taskId: string; limit?: number; offset?: number; silent?: boolean }, { rejectWithValue }) => {
    try {
      const { taskId, limit = 20, offset = 0 } = payload
      const res = await axios.get(`${scheduledTasksUrl}/${taskId}/runs?limit=${limit}&offset=${offset}`, getAxiosDefaultConfig())
      return { taskId, runs: res?.data?.runs ?? [], total: res?.data?.total ?? 0 }
    } catch (err) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const fetchTaskRunDetail = createAsyncThunk(
  'openai/fetchTaskRunDetail',
  async (payload: { taskId: string; runId: string }, { rejectWithValue }) => {
    try {
      const { taskId, runId } = payload
      const res = await axios.get(`${scheduledTasksUrl}/${taskId}/runs/${runId}`, getAxiosDefaultConfig())
      return { taskId, runId, run: res?.data }
    } catch (err) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const synthesizeTaskIntent = createAsyncThunk(
  'openai/synthesizeTaskIntent',
  async (
    payload: {
      conversationHistory: any[]
      messageId?: string
      toolName?: string
      toolParameters?: any
      userInstructions?: string
      currentTools?: any[]
      currentTaskContext?: Record<string, any>
      agentSteps?: any[]
      /** Not sent to the API; stored on success for cache invalidation. */
      clientSynthesisKey?: string
      /** Not sent; when true, keep previous taskIntentSynthesis until fulfilled (refine in flight). */
      refinement?: boolean
    },
    { rejectWithValue }
  ) => {
    try {
      const body = { ...payload } as Record<string, unknown>
      delete body.clientSynthesisKey
      delete body.refinement
      const res = await axios.post(`${scheduledTasksUrl}/synthesize-intent`, body, getAxiosDefaultConfig())
      return res?.data
    } catch (err) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const suggestScheduledTask = createAsyncThunk(
  'openai/suggestScheduledTask',
  async (
    payload: {
      messageId: string
      conversationHistory: any[]
      agentSteps: any[]
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await axios.post(
        `${scheduledTasksUrl}/suggest`,
        {
          conversationHistory: payload.conversationHistory,
          agentSteps: payload.agentSteps,
        },
        getAxiosDefaultConfig()
      )
      return {
        messageId: payload.messageId,
        suggest: Boolean(res?.data?.suggest),
        taskName: res?.data?.taskName ?? '',
        schedule: res?.data?.schedule ?? 'weekly',
        reason: res?.data?.reason ?? '',
      }
    } catch (err) {
      return rejectWithValue({ messageId: payload.messageId })
    }
  }
)

// ---- Pre-built tasks (Task Library) ----

export const fetchPreBuiltTasks = createAsyncThunk(
  'openai/fetchPreBuiltTasks',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(preBuiltTasksUrl, getAxiosDefaultConfig())
      return res?.data?.tasks ?? []
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const fetchPreBuiltTaskBySlug = createAsyncThunk(
  'openai/fetchPreBuiltTaskBySlug',
  async (slug: string, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${preBuiltTasksUrl}/${encodeURIComponent(slug)}`, getAxiosDefaultConfig())
      return { slug, task: res?.data }
    } catch (err: any) {
      return rejectWithValue({ slug, error: err?.response?.data || err })
    }
  }
)

// The detail endpoint accepts either slug or UUID, so this thunk simply targets
// the same route by id. Used by "Chat Now" deep-links from the Task Library where
// the caller has the row id but not the slug.
export const fetchPreBuiltTaskById = createAsyncThunk(
  'openai/fetchPreBuiltTaskById',
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${preBuiltTasksUrl}/${encodeURIComponent(id)}`, getAxiosDefaultConfig())
      return res?.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const installPreBuiltTask = createAsyncThunk(
  'openai/installPreBuiltTask',
  async (
    payload: {
      slug: string
      key_id: string
      schedule_type?: string
      time?: string
      timezone?: string
      day_of_week?: number
      day_of_month?: number
      delivery_config?: Record<string, any>
    },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const { slug, ...body } = payload
      const res = await axios.post(
        `${preBuiltTasksUrl}/${encodeURIComponent(slug)}/install`,
        body,
        getAxiosDefaultConfig()
      )
      void dispatch(fetchScheduledTasks() as any)
      return res?.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const fetchAdminPreBuiltTasks = createAsyncThunk(
  'openai/fetchAdminPreBuiltTasks',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(adminPreBuiltTasksUrl, getAxiosDefaultConfig())
      return res?.data?.tasks ?? []
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const createPreBuiltTask = createAsyncThunk(
  'openai/createPreBuiltTask',
  async (payload: Record<string, any>, { rejectWithValue }) => {
    try {
      const res = await axios.post(adminPreBuiltTasksUrl, payload, getAxiosDefaultConfig())
      return res?.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const updatePreBuiltTask = createAsyncThunk(
  'openai/updatePreBuiltTask',
  async (payload: { id: string; [k: string]: any }, { rejectWithValue }) => {
    try {
      const { id, ...body } = payload
      const res = await axios.put(`${adminPreBuiltTasksUrl}/${id}`, body, getAxiosDefaultConfig())
      return res?.data
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const deletePreBuiltTask = createAsyncThunk(
  'openai/deletePreBuiltTask',
  async (id: string, { rejectWithValue }) => {
    try {
      await axios.delete(`${adminPreBuiltTasksUrl}/${id}`, getAxiosDefaultConfig())
      return id
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const createPreBuiltTaskFromScheduledTask = createAsyncThunk(
  'openai/createPreBuiltTaskFromScheduledTask',
  async (payload: { scheduledTaskId: string; enrich?: boolean }, { rejectWithValue }) => {
    try {
      const { scheduledTaskId, enrich = false } = payload
      const res = await axios.post(
        `${adminPreBuiltTasksUrl}/from-task/${scheduledTaskId}`,
        { enrich },
        getAxiosDefaultConfig()
      )
      return res?.data?.draft
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const draftPreBuiltTaskFromPrompt = createAsyncThunk(
  'openai/draftPreBuiltTaskFromPrompt',
  async (payload: { prompt: string }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${adminPreBuiltTasksUrl}/draft-from-prompt`,
        { prompt: payload.prompt },
        getAxiosDefaultConfig()
      )
      return res?.data?.draft
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const enrichPreBuiltTaskFields = createAsyncThunk(
  'openai/enrichPreBuiltTaskFields',
  async (
    payload: { draft: any; fields: string[]; step_index?: number | null },
    { rejectWithValue }
  ) => {
    try {
      const res = await axios.post(
        `${adminPreBuiltTasksUrl}/enrich`,
        {
          draft: payload.draft,
          fields: payload.fields,
          ...(payload.step_index != null ? { step_index: payload.step_index } : {}),
        },
        getAxiosDefaultConfig()
      )
      return res?.data?.draft || {}
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const suggestPreBuiltTaskToolPipeline = createAsyncThunk(
  'openai/suggestPreBuiltTaskToolPipeline',
  async (
    payload: { name?: string; summary?: string; description?: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await axios.post(
        `${adminPreBuiltTasksUrl}/suggest-tool-pipeline`,
        {
          name: payload.name,
          summary: payload.summary,
          description: payload.description,
        },
        getAxiosDefaultConfig()
      )
      return (res?.data?.tools as any[]) || []
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const generateExampleOutputFromRuns = createAsyncThunk(
  'openai/generateExampleOutputFromRuns',
  async (payload: { scheduledTaskId: string }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${adminPreBuiltTasksUrl}/example-output-from-runs/${payload.scheduledTaskId}`,
        {},
        getAxiosDefaultConfig()
      )
      return res?.data?.example_output || ''
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const fetchToolAccess = createAsyncThunk(
  'openai/fetchToolAccess',
  async (
    payload: { from?: string; to?: string; rangeKey?: '7d' | '30d' | 'mtd' | 'all' } | undefined,
    { rejectWithValue }
  ) => {
    try {
      const params: Record<string, string> = {}
      if (payload?.from) params.from = payload.from
      if (payload?.to) params.to = payload.to
      const res = await axios.get(`${openAIUrl}/chat/tool-access`, {
        ...getAxiosDefaultConfig(),
        params,
      })
      return {
        invocations: res?.data?.invocations || [],
        summary: res?.data?.summary || null,
        rangeKey: payload?.rangeKey || '30d',
      }
    } catch (err: any) {
      return rejectWithValue(err?.response?.data || err)
    }
  }
)

export const openaiSlice = createSlice({
  name: 'openai',
  initialState,
  reducers: {
    toggleChatBox: (state) => {
      state.chatBoxIsExpanded = !state.chatBoxIsExpanded
    },
    setChatBoxExpanded: (state, action) => {
      state.chatBoxIsExpanded = action.payload
    },
    setMessageId: (state, action) => {
      state.messageId = action.payload
    },
    clearCurrentChat: (state) => {
      // This will be used by the main AI component to clear current messages
    },
    // Agent mode reducers - persist to localStorage and debounced DB save
    toggleAgentMode: (state) => {
      state.agentModeEnabled = !state.agentModeEnabled
      saveAgentMode(state.agentModeEnabled)
      persistAgentSettings({
        model: state.agentModel,
        effortLevel: state.effortLevel,
        agentModeEnabled: state.agentModeEnabled,
      })
    },
    setAgentModeEnabled: (state, action) => {
      state.agentModeEnabled = action.payload
      saveAgentMode(action.payload)
      persistAgentSettings({
        model: state.agentModel,
        effortLevel: state.effortLevel,
        agentModeEnabled: state.agentModeEnabled,
      })
    },
    setEffortLevel: (state, action) => {
      // The picker only offers levels the catalog listed.
      if (!Number.isInteger(action.payload)) return
      const level = action.payload
      state.effortLevel = level
      saveEffortLevel(level)
      persistAgentSettings({
        model: state.agentModel,
        effortLevel: state.effortLevel,
        agentModeEnabled: state.agentModeEnabled,
      })
    },
    setKnowledgeSources: (state, action) => {
      const { sourcePage, sources } = action.payload
      state.agentKnowledgeSources[sourcePage] = Array.isArray(sources) ? sources : []
    },
    clearKnowledgeSources: (state, action) => {
      const { sourcePage } = action.payload
      delete state.agentKnowledgeSources[sourcePage]
    },
    setGeneratedPdfs: (state, action) => {
      const { sourcePage, pdfs } = action.payload
      state.agentGeneratedPdfs[sourcePage] = Array.isArray(pdfs) ? pdfs : []
    },
    clearGeneratedPdfs: (state, action) => {
      const { sourcePage } = action.payload
      delete state.agentGeneratedPdfs[sourcePage]
    },
    // Bound per-chat streaming buckets so switching between many chats doesn't leak memory.
    // Keeps only the buckets whose sourcePage is in keepIds (plus the legacy global 'ai').
    pruneInsightBuckets: (state, action) => {
      const keep = new Set([...(action.payload?.keepIds ?? []), 'ai'])
      const buckets = [
        'insightResponse', 'insightStatus', 'insightError',
        'agentSteps', 'agentStatus', 'agentCurrentStep', 'agentMemoryUpdate',
        'agentNarrations', 'agentAvailableTools', 'agentCsuiteReviews', 'agentCsuiteStatus',
        'agentKnowledgeSources',
        'agentGeneratedPdfs',
      ]
      for (const bucket of buckets) {
        const map = state[bucket]
        if (!map || typeof map !== 'object') continue
        for (const key of Object.keys(map)) {
          if (!keep.has(key)) delete map[key]
        }
      }
    },
    setImageModel: (state, action) => {
      const catalog = state.agentCatalog.data
      const model = isImageModelInCatalog(catalog, action.payload) ? action.payload : catalog.defaultImageModel
      state.imageModel = model
      saveImageModel(model)
    },
    setAgentModel: (state, action) => {
      const catalog = state.agentCatalog.data
      const model = isModelInCatalog(catalog, action.payload) ? action.payload : catalog.defaultModel
      state.agentModel = model
      saveAgentModel(model)
      persistAgentSettings({
        model: state.agentModel,
        effortLevel: state.effortLevel,
        agentModeEnabled: state.agentModeEnabled,
      })
    },
    addAgentStep: (state, action) => {
      const { sourcePage, step } = action.payload
      if (!state.agentSteps[sourcePage]) {
        state.agentSteps[sourcePage] = []
      }

      // Deduplicate steps on the frontend as a safety net
      const existingSteps = state.agentSteps[sourcePage]

      // For thinking steps (no tool), check if we have a similar thought
      if (!step.tool && step.thought) {
        const normalizedThought = step.thought.trim().slice(0, 100).toLowerCase()
        const isDupe = existingSteps.some(s =>
          !s.tool && s.thought &&
          s.thought.trim().slice(0, 100).toLowerCase() === normalizedThought
        )
        if (isDupe) {
          return // Don't add duplicate
        }
      }

      // For tool steps, check if we have the same tool with same query
      if (step.tool) {
        const queryStr = JSON.stringify(step.toolInput?.query || step.toolInput || '')
        const isDupe = existingSteps.some(s =>
          s.tool === step.tool &&
          JSON.stringify(s.toolInput?.query || s.toolInput || '') === queryStr
        )
        if (isDupe) {
          return // Don't add duplicate
        }
      }

      state.agentSteps[sourcePage].push(step)
      state.agentCurrentStep[sourcePage] = state.agentSteps[sourcePage].length - 1
    },
    updateAgentStep: (state, action) => {
      const { sourcePage, iteration, tool, toolUseId, updates } = action.payload
      const steps = state.agentSteps[sourcePage]
      if (!steps) return

      // Find the step by matching - priority: toolUseId > (iteration + tool) > iteration
      let stepIndex = -1

      // First try matching by toolUseId (most reliable)
      if (toolUseId) {
        for (let i = steps.length - 1; i >= 0; i--) {
          if ((steps[i] as any).toolUseId === toolUseId) {
            stepIndex = i
            break
          }
        }
      }

      // Fallback: match by iteration and tool name
      if (stepIndex < 0 && tool) {
        for (let i = steps.length - 1; i >= 0; i--) {
          if (steps[i].iteration === iteration && steps[i].tool === tool && steps[i].status === 'running') {
            stepIndex = i
            break
          }
        }
      }

      // Fallback: for tool steps without tool name, find any running tool step at this iteration
      if (stepIndex < 0 && !tool) {
        for (let i = steps.length - 1; i >= 0; i--) {
          if (steps[i].iteration === iteration && steps[i].tool && steps[i].status === 'running') {
            stepIndex = i
            break
          }
        }
      }

      // Final fallback: non-tool steps (thoughts), find by iteration
      if (stepIndex < 0 && !tool) {
        for (let i = steps.length - 1; i >= 0; i--) {
          if (steps[i].iteration === iteration && !steps[i].tool) {
            stepIndex = i
            break
          }
        }
      }

      if (stepIndex >= 0) {
        // Only apply defined updates — never overwrite existing values with undefined
        // This is critical: tool result updates don't include toolInput/thought,
        // so spreading undefined would wipe the original query data
        const definedUpdates: Record<string, any> = {}
        for (const [key, value] of Object.entries(updates)) {
          if (value !== undefined && value !== null) {
            definedUpdates[key] = value
          }
        }
        state.agentSteps[sourcePage][stepIndex] = {
          ...state.agentSteps[sourcePage][stepIndex],
          ...definedUpdates
        }
      }
    },
    setAgentStatus: (state, action) => {
      const { sourcePage, status } = action.payload
      state.agentStatus[sourcePage] = status
      // NOTE: Don't clean up running steps here. The chain of thought component
      // uses running steps to keep the progress indicator visible. Steps will
      // get their proper completion status via tool result SSE events.
    },
    clearAgentSteps: (state, action) => {
      const { sourcePage } = action.payload
      state.agentSteps[sourcePage] = []
      state.agentCurrentStep[sourcePage] = 0
      state.agentStatus[sourcePage] = 'idle'
      state.agentMemoryUpdate[sourcePage] = null
      state.agentNarrations[sourcePage] = []
      state.agentCsuiteReviews[sourcePage] = []
      state.agentCsuiteStatus[sourcePage] = 'idle'
      // Do not reset insightStatus / insightResponse here — askAgentForInsight dispatches
      // clearAgentSteps after pending sets loading; clearing insight would hide Stop and
      // let stale streamed text overwrite the new bubble. Use resetInsightForSourcePage
      // when leaving a chat (new chat / history) instead.
    },
    /** Remember the SDK session for a chat so follow-ups can resume it. */
    setAgentCreditSummary: (state, action) => {
      const { sourcePage, creditSummary } = action.payload
      if (sourcePage) {
        state.agentCreditSummary[sourcePage] = creditSummary || null
      }
    },
    setAgentSessionId: (state, action) => {
      const { chatId, sessionId } = action.payload
      if (chatId && sessionId) {
        state.agentSessionIds[chatId] = sessionId
      }
    },
    resetInsightForSourcePage: (state, action) => {
      const { sourcePage } = action.payload
      state.insightResponse[sourcePage] = ''
      state.insightStatus[sourcePage] = 'idle'
      state.insightError[sourcePage] = null
    },
    /** Flag a conversation (by message_id) as having an in-progress turn. */
    markChatLoading: (state, action) => {
      const chatId = action.payload
      if (chatId && !state.activeChatIds.includes(chatId)) {
        state.activeChatIds.push(chatId)
      }
    },
    /** Clear the in-progress flag for a conversation (completed / cancelled / failed). */
    markChatIdle: (state, action) => {
      const chatId = action.payload
      if (chatId) {
        state.activeChatIds = state.activeChatIds.filter((id: string) => id !== chatId)
      }
    },
    /** Add or move-to-top a chat history list item. Used to surface a brand-new
     *  in-progress conversation in the history list before the server persists it. */
    upsertChatHistoryItem: (state, action) => {
      const item = action.payload
      if (!item || !item.message_id) return
      const data: any[] = Array.isArray(state.chatHistory.data) ? state.chatHistory.data : []
      const idx = data.findIndex((c: any) => c?.message_id === item.message_id)
      if (idx >= 0) {
        const existing = data[idx]
        data.splice(idx, 1)
        data.unshift({ ...existing, ...item })
      } else {
        data.unshift(item)
      }
      state.chatHistory.data = data
    },
    setAgentMemoryUpdate: (state, action) => {
      const { sourcePage, memoryUpdate } = action.payload
      state.agentMemoryUpdate[sourcePage] = memoryUpdate
    },
    clearAgentMemoryUpdate: (state, action) => {
      const { sourcePage } = action.payload
      state.agentMemoryUpdate[sourcePage] = null
    },
    addAgentNarration: (state, action) => {
      const { sourcePage, narration } = action.payload
      if (!state.agentNarrations[sourcePage]) {
        state.agentNarrations[sourcePage] = []
      }
      // onDownloadProgress replays previously seen SSE chunks. Deduplicate by
      // stable narration payload fields so replayed chunks don't spam the UI.
      const exists = state.agentNarrations[sourcePage].some((n: any) =>
        n?.text === narration?.text &&
        n?.atStep === narration?.atStep &&
        (n?.workedForMs ?? 0) === (narration?.workedForMs ?? 0)
      )
      if (exists) {
        return
      }
      state.agentNarrations[sourcePage].push(narration)
    },
    clearAgentNarrations: (state, action) => {
      const { sourcePage } = action.payload
      state.agentNarrations[sourcePage] = []
    },
    setAgentAvailableTools: (state, action) => {
      const { sourcePage, tools } = action.payload
      state.agentAvailableTools[sourcePage] = tools
    },
    // C-Suite review reducers
    setCsuiteReviewStatus: (state, action) => {
      const { sourcePage, status } = action.payload
      state.agentCsuiteStatus[sourcePage] = status
    },
    addCsuiteReviewStep: (state, action) => {
      const { sourcePage, step } = action.payload
      if (!state.agentCsuiteReviews[sourcePage]) {
        state.agentCsuiteReviews[sourcePage] = []
      }
      // Upsert: update existing step for same role, or add new
      const existingIdx = state.agentCsuiteReviews[sourcePage]
        .findIndex((s: any) => s.role === step.role)
      if (existingIdx >= 0) {
        state.agentCsuiteReviews[sourcePage][existingIdx] = {
          ...state.agentCsuiteReviews[sourcePage][existingIdx],
          ...step,
        }
      } else {
        state.agentCsuiteReviews[sourcePage].push(step)
      }
    },
    clearCsuiteReviews: (state, action) => {
      const { sourcePage } = action.payload
      state.agentCsuiteReviews[sourcePage] = []
      state.agentCsuiteStatus[sourcePage] = 'idle'
    },
    setSelectedTaskId: (state, action) => {
      state.selectedTaskId = action.payload
    },
    dismissTaskSuggestion: (state, action) => {
      const messageId = action.payload as string
      const cur = state.taskSuggestions[messageId]
      if (cur) {
        state.taskSuggestions[messageId] = { ...cur, dismissed: true }
      }
    },
    clearTaskSuggestions: (state) => {
      state.taskSuggestions = {}
    },
    clearTaskIntentSynthesis: (state) => {
      state.taskIntentSynthesis = null
      state.taskIntentStatus = 'idle'
      state.taskIntentSynthesisKey = null
      state.taskIntentSynthesisInFlightKey = null
    },
    setActivePreBuiltTaskContext: (state, action) => {
      const task = action.payload
      if (!task || typeof task !== 'object') {
        state.activePreBuiltTaskContext = null
        return
      }
      const summary = (task.summary || '').toString().trim() || null
      const description = (task.description || '').toString().trim() || null
      const objective = summary || description || null
      // Keep only the lightweight, non-secret fields the agent needs as context.
      state.activePreBuiltTaskContext = {
        id: task.id,
        name: task.name,
        objective,
        summary,
        description,
      }
    },
    clearActivePreBuiltTaskContext: (state) => {
      state.activePreBuiltTaskContext = null
    },
  },
  extraReducers(builder) {
    builder
      /**
       *
       * OpenAI account
       *
       */
      .addCase(fetchOpenAIAccount.pending, (state, action) => {
        state.account.status = 'loading'
      })
      .addCase(fetchOpenAIAccount.fulfilled, (state, action) => {
        state.account.status = 'fulfilled'
        state.account.data = action.payload
      })
      .addCase(fetchOpenAIAccount.rejected, (state, action) => {
        state.account.status = 'failed'
        state.account.data = action.error.message
      })
      .addCase(createOpenAIAccount.fulfilled, (state, action) => {
        state.account.data = action.payload
      })
      .addCase(updateOpenAIAccount.fulfilled, (state, action) => {
        state.account.data = action.payload
      })
      .addCase(deleteOpenAIAccount.fulfilled, (state, action) => {
        state.account.data = action.payload
      })

      /**
       *
       * OpenAI Chat API
       *
       */
      .addCase(callOpenAIChat.pending, (state, action) => {
        state.chat.status = 'loading'
      })
      .addCase(callOpenAIChat.fulfilled, (state, action) => {
        state.chat.status = 'succeeded'
        state.chat.data = action.payload
      })
      .addCase(callOpenAIChat.rejected, (state, action) => {
        state.chat.status = 'failed'
        state.chat.data = action.payload
      })

      /**
       *
       * OpenAI Chat History API
       *
       */
      // ── Chat history (initial fetch) ──────────────────────────────────────
      .addCase(fetchOpenAIChatHistory.pending, (state) => {
        state.chatHistory.status = 'loading'
        state.chatHistory.error = null
      })
      .addCase(fetchOpenAIChatHistory.fulfilled, (state, action) => {
        state.account.status = 'fulfilled'
        state.chatHistory.status = 'succeeded'
        // Ensure chatHistory is a proper object in case old persisted state has a different shape
        const items = Array.isArray(action.payload.items) ? action.payload.items : []
        state.chatHistory.data = items
        state.chatHistory.hasMore = Boolean(action.payload.hasMore)
        state.chatHistory.offset = items.length
      })
      .addCase(fetchOpenAIChatHistory.rejected, (state, action) => {
        state.chatHistory.status = 'failed'
        state.chatHistory.error = action.payload
      })

      // ── Chat history (load more) ──────────────────────────────────────────
      .addCase(fetchMoreOpenAIChatHistory.pending, (state) => {
        state.chatHistory.loadingMore = true
      })
      .addCase(fetchMoreOpenAIChatHistory.fulfilled, (state, action) => {
        state.chatHistory.loadingMore = false
        // Guard against stale persisted state where data may not be an array
        const current: any[] = Array.isArray(state.chatHistory.data) ? state.chatHistory.data : []
        const existingIds = new Set(current.map((i: any) => i.message_id))
        const newItems = action.payload.items.filter((i: any) => !existingIds.has(i.message_id))
        state.chatHistory.data = [...current, ...newItems]
        state.chatHistory.hasMore = action.payload.hasMore
        state.chatHistory.offset = state.chatHistory.data.length
      })
      .addCase(fetchMoreOpenAIChatHistory.rejected, (state) => {
        state.chatHistory.loadingMore = false
      })

      // ── Delete / delete-all ───────────────────────────────────────────────
      .addCase(deleteOpenAIChatHistory.pending, (state) => {
        state.chatHistory.status = 'deleting'
      })
      .addCase(deleteOpenAIChatHistory.fulfilled, (state) => {
        state.chatHistory.status = 'succeeded'
      })
      .addCase(deleteOpenAIChatHistory.rejected, (state, action) => {
        state.chatHistory.status = 'failed'
        state.chatHistory.error = action.payload
      })
      .addCase(deleteAllOpenAIChatHistory.pending, (state) => {
        state.chatHistory.status = 'deleting'
      })
      .addCase(deleteAllOpenAIChatHistory.fulfilled, (state) => {
        state.chatHistory.status = 'succeeded'
        state.chatHistory.data = []
        state.chatHistory.hasMore = false
        state.chatHistory.offset = 0
      })
      .addCase(deleteAllOpenAIChatHistory.rejected, (state, action) => {
        state.chatHistory.status = 'failed'
        state.chatHistory.error = action.payload
      })

      // ── Privacy update ────────────────────────────────────────────────────
      .addCase(updateOpenAIChatPrivacy.pending, (state) => {
        state.chatHistory.status = 'updating'
      })
      .addCase(updateOpenAIChatPrivacy.fulfilled, (state) => {
        state.chatHistory.status = 'succeeded'
      })
      .addCase(updateOpenAIChatPrivacy.rejected, (state, action) => {
        state.chatHistory.status = 'failed'
        state.chatHistory.error = action.payload
      })

      // ── Model / effort catalog ────────────────────────────────────────────
      .addCase(fetchAgentModelCatalog.pending, (state) => {
        state.agentCatalog.status = 'loading'
        state.agentCatalog.error = null
      })
      .addCase(fetchAgentModelCatalog.fulfilled, (state, action) => {
        state.agentCatalog.status = 'succeeded'
        state.agentCatalog.data = action.payload
        state.agentCatalog.error = null
        reconcileAgentSelection(state)
      })
      .addCase(fetchAgentModelCatalog.rejected, (state, action) => {
        // Keep whatever catalog is already in state (fallback or a previous fetch).
        state.agentCatalog.status = 'failed'
        state.agentCatalog.error = action.payload
      })

      // ── Pin / unpin ───────────────────────────────────────────────────────
      .addCase(togglePinOpenAIChatHistory.pending, (state) => {
        state.chatHistory.status = 'updating'
      })
      .addCase(togglePinOpenAIChatHistory.fulfilled, (state) => {
        state.chatHistory.status = 'succeeded'
      })
      .addCase(togglePinOpenAIChatHistory.rejected, (state, action) => {
        state.chatHistory.status = 'failed'
        state.chatHistory.error = action.payload
      })

      .addCase(fetchRelatedQuestions.pending, (state, action) => {
        const { sourcePage } = action.meta.arg
        state.relatedQuestionsStatus[sourcePage] = 'loading'
        state.relatedQuestionsError[sourcePage] = null
      })
      .addCase(fetchRelatedQuestions.fulfilled, (state, action) => {
        const { sourcePage } = action.meta.arg
        state.relatedQuestionsStatus[sourcePage] = 'succeeded'
        state.relatedQuestions[sourcePage] = action.payload
        state.relatedQuestionsError[sourcePage] = null
      })
      .addCase(fetchRelatedQuestions.rejected, (state, action) => {
        const { sourcePage } = action.meta.arg
        state.relatedQuestionsStatus[sourcePage] = 'failed'
        state.relatedQuestionsError[sourcePage] = action.payload
      })

      .addCase(updateInsightChunk.fulfilled, (state, action) => {
        const { sourcePage } = action.meta.arg
        // Extract just the data content, not the whole payload object
        state.insightResponse[sourcePage] = action.payload?.data || ''
      })

      .addCase(askForInsight.pending, (state, action) => {
        const { sourcePage } = action.meta.arg
        state.insightStatus[sourcePage] = 'loading'
        state.insightError[sourcePage] = null
      })
      .addCase(askForInsight.fulfilled, (state, action) => {
        const { sourcePage } = action.meta.arg
        state.insightStatus[sourcePage] = 'succeeded'
        // Don't clear insightResponse - it was set during streaming and the component
        // effect needs it to add to chatHistory. Clearing causes a race condition.
        state.insightError[sourcePage] = null
      })
      .addCase(askForInsight.rejected, (state, action) => {
        const { sourcePage } = action.meta.arg
        state.insightStatus[sourcePage] = 'failed'
        state.insightError[sourcePage] = action.payload
      })

      // Agent mode cases
      .addCase(askAgentForInsight.pending, (state, action) => {
        const { sourcePage, conversationHistory } = action.meta.arg
        const isFollowUp = conversationHistory && conversationHistory.length > 0

        state.insightStatus[sourcePage] = 'loading'
        state.insightError[sourcePage] = null
        state.insightResponse[sourcePage] = ''
        state.agentStatus[sourcePage] = 'running'
        // #935: drop the previous turn's cost summary. It is attached to a
        // message as soon as it is present, so a stale value would land on the
        // new answer before its own summary arrives.
        state.agentCreditSummary[sourcePage] = null

        // Only clear agent steps for NEW conversations, not follow-ups
        // This preserves the data dashboard when asking follow-up questions like "do #2"
        if (!isFollowUp) {
          state.agentSteps[sourcePage] = []
        }
      })
      .addCase(askAgentForInsight.fulfilled, (state, action) => {
        const { sourcePage } = action.meta.arg
        state.insightStatus[sourcePage] = 'succeeded'
        state.insightError[sourcePage] = null
      })
      .addCase(askAgentForInsight.rejected, (state, action) => {
        const { sourcePage } = action.meta.arg
        const payload = action.payload as { cancelled?: boolean } | undefined
        if (payload?.cancelled) {
          state.insightStatus[sourcePage] = 'idle'
          state.insightError[sourcePage] = null
          state.agentStatus[sourcePage] = 'cancelled'
          // Keep insightResponse — UI finalizes the bubble from local state; partial stream text stays visible.
          return
        }
        state.insightStatus[sourcePage] = 'failed'
        state.insightError[sourcePage] = action.payload
        state.agentStatus[sourcePage] = 'failed'
      })
      // Chat preferences thunks
      .addCase(fetchChatPreferences.pending, (state) => {
        state.chatPreferences.status = 'loading'
        state.chatPreferences.error = null
      })
      .addCase(fetchChatPreferences.fulfilled, (state, action) => {
        state.chatPreferences.status = 'succeeded'
        state.chatPreferences.lastActiveChatId = action.payload?.preferences?.lastActiveChatId || null
        state.chatPreferences.autoLoadEnabled = action.payload?.preferences?.autoLoadEnabled ?? true
        state.chatPreferences.interruptedSessions = action.payload?.interruptedSessions || []
        state.chatPreferences.error = null
      })
      .addCase(fetchChatPreferences.rejected, (state, action) => {
        state.chatPreferences.status = 'failed'
        state.chatPreferences.error = action.payload
      })
      .addCase(updateChatPreferences.fulfilled, (state, action) => {
        if (action.payload?.lastActiveChatId !== undefined) {
          state.chatPreferences.lastActiveChatId = action.payload.lastActiveChatId
        }
        if (action.payload?.autoLoadEnabled !== undefined) {
          state.chatPreferences.autoLoadEnabled = action.payload.autoLoadEnabled
        }
      })
      .addCase(fetchAgentSettings.fulfilled, (state, action) => {
        const settings = action.payload
        if (!settings || typeof settings !== 'object') return

        const catalog = state.agentCatalog.data
        const catalogLoaded = state.agentCatalog.status === 'succeeded'
        // Accept the DB value while only the bundled fallback is loaded: it may
        // name a model this build predates, and dropping it here would lose the
        // user's choice (this fetch also runs from AIChatBox, which does not
        // wait for the catalog). reconcileAgentSelection has the final say.
        if (typeof settings.model === 'string' && settings.model.trim()) {
          if (!catalogLoaded || isModelInCatalog(catalog, settings.model)) {
            state.agentModel = settings.model.trim()
            saveAgentModel(state.agentModel)
          }
        }

        // Effort level (1-5) replaces the old autonomyLevel; fall back to a
        // legacy autonomyLevel value so existing persisted settings still apply.
        const rawEffort = settings.effortLevel ?? settings.autonomyLevel
        const level = Number(rawEffort)
        if (Number.isInteger(level)) {
          state.effortLevel = level
          saveEffortLevel(level)
        }

        if (typeof settings.agentModeEnabled === 'boolean') {
          state.agentModeEnabled = settings.agentModeEnabled
          saveAgentMode(settings.agentModeEnabled)
        }

        // Settings can land after the catalog, so re-check here too.
        reconcileAgentSelection(state)
      })

      .addCase(fetchCustomTools.pending, (state) => {
        state.customTools.status = 'loading'
        state.customTools.error = null
      })
      .addCase(fetchCustomTools.fulfilled, (state, action) => {
        state.customTools.status = 'succeeded'
        state.customTools.items = action.payload || []
        state.customTools.error = null
      })
      .addCase(fetchCustomTools.rejected, (state, action) => {
        state.customTools.status = 'failed'
        state.customTools.error = action.payload
      })
      .addCase(createCustomTool.fulfilled, (state, action) => {
        if (action.payload) state.customTools.items = [...state.customTools.items, action.payload].sort((a: any, b: any) => a.name.localeCompare(b.name))
      })
      .addCase(updateCustomTool.fulfilled, (state, action) => {
        if (!action.payload) return
        const id = action.payload.id
        state.customTools.items = state.customTools.items.map((t: any) => (t.id === id ? action.payload : t))
      })
      .addCase(deleteCustomTool.fulfilled, (state, action) => {
        state.customTools.items = state.customTools.items.filter((t: any) => t.id !== action.payload)
      })
      .addCase(confirmOpenApiImport.fulfilled, (state) => {
        // list refreshed by caller via fetchCustomTools
      })

      // Scheduled tasks
      .addCase(fetchScheduledTasks.pending, (state) => {
        state.scheduledTasksStatus = 'loading'
      })
      .addCase(fetchScheduledTasks.fulfilled, (state, action) => {
        state.scheduledTasksStatus = 'succeeded'
        state.scheduledTasks = action.payload ?? []
      })
      .addCase(fetchScheduledTasks.rejected, (state) => {
        state.scheduledTasksStatus = 'failed'
      })
      .addCase(createScheduledTask.fulfilled, (state, action) => {
        if (action.payload) state.scheduledTasks = [action.payload, ...state.scheduledTasks]
      })
      .addCase(updateScheduledTask.fulfilled, (state, action) => {
        if (action.payload) {
          const idx = state.scheduledTasks.findIndex((t: any) => t.id === action.payload.id)
          if (idx >= 0) state.scheduledTasks[idx] = action.payload
        }
      })
      .addCase(deleteScheduledTask.fulfilled, (state, action) => {
        state.scheduledTasks = state.scheduledTasks.filter((t: any) => t.id !== action.payload)
        if (state.selectedTaskId === action.payload) state.selectedTaskId = null
      })
      .addCase(toggleScheduledTask.fulfilled, (state, action) => {
        if (action.payload) {
          const idx = state.scheduledTasks.findIndex((t: any) => t.id === action.payload.id)
          if (idx >= 0) state.scheduledTasks[idx] = action.payload
        }
      })
      .addCase(reorderScheduledTasks.fulfilled, (state, action) => {
        const updated = action.payload ?? []
        if (!Array.isArray(updated) || updated.length === 0) return
        const byId = new Map(updated.map((t: any) => [t.id, t]))
        state.scheduledTasks = state.scheduledTasks
          .map((t: any) => byId.get(t.id) || t)
          .sort((a: any, b: any) => {
            const ao = a.sort_order ?? Number.MAX_SAFE_INTEGER
            const bo = b.sort_order ?? Number.MAX_SAFE_INTEGER
            if (ao !== bo) return ao - bo
            return String(b.created_at || '').localeCompare(String(a.created_at || ''))
          })
      })
      .addCase(fetchTaskRuns.pending, (state, action) => {
        if (!action.meta.arg?.silent) state.taskRunsStatus = 'loading'
      })
      .addCase(fetchTaskRuns.fulfilled, (state, action) => {
        state.taskRunsStatus = 'succeeded'
        const { taskId, runs, total } = action.payload
        const existing = state.taskRuns[taskId]?.runs || []
        const prevById = new Map(existing.map((r: any) => [r.id, r]))
        const merged = (runs || []).map((r: any) => {
          const prev = prevById.get(r.id)
          if (!prev) return r
          return {
            ...prev,
            ...r,
            response_data: r.response_data ?? prev.response_data,
            resolved_parameters: r.resolved_parameters ?? prev.resolved_parameters,
          }
        })
        state.taskRuns[taskId] = { runs: merged, total }
      })
      .addCase(fetchTaskRuns.rejected, (state, action) => {
        if (!action.meta.arg?.silent) state.taskRunsStatus = 'failed'
      })
      .addCase(fetchTaskRunDetail.fulfilled, (state, action) => {
        const { taskId, runId, run } = action.payload
        if (!state.taskRuns[taskId]) state.taskRuns[taskId] = { runs: [], total: 0 }
        const runList = state.taskRuns[taskId].runs
        const i = runList.findIndex((r: any) => r.id === runId)
        if (i >= 0) runList[i] = run
        else runList.push(run)
      })
      .addCase(synthesizeTaskIntent.pending, (state, action) => {
        state.taskIntentStatus = 'loading'
        if (!action.meta.arg?.refinement) {
          state.taskIntentSynthesis = null
        }
        state.taskIntentSynthesisInFlightKey = action.meta.arg?.clientSynthesisKey ?? null
      })
      .addCase(synthesizeTaskIntent.fulfilled, (state, action) => {
        state.taskIntentStatus = 'succeeded'
        state.taskIntentSynthesis = action.payload
        state.taskIntentSynthesisInFlightKey = null
        const key = action.meta.arg?.clientSynthesisKey
        state.taskIntentSynthesisKey = key != null && key !== '' ? key : null
      })
      .addCase(synthesizeTaskIntent.rejected, (state, action) => {
        state.taskIntentStatus = 'failed'
        if (!action.meta.arg?.refinement) {
          state.taskIntentSynthesis = null
          state.taskIntentSynthesisKey = null
        }
        state.taskIntentSynthesisInFlightKey = null
      })
      .addCase(suggestScheduledTask.pending, (state, action) => {
        const messageId = action.meta.arg.messageId
        state.taskSuggestions[messageId] = {
          ...state.taskSuggestions[messageId],
          loading: true,
          suggest: false,
          taskName: '',
          schedule: 'weekly',
          reason: '',
        }
      })
      .addCase(suggestScheduledTask.fulfilled, (state, action) => {
        const { messageId, suggest, taskName, schedule, reason } = action.payload
        state.taskSuggestions[messageId] = {
          loading: false,
          suggest,
          taskName: taskName || '',
          schedule: schedule || 'weekly',
          reason: reason || '',
          dismissed: false,
        }
      })
      .addCase(suggestScheduledTask.rejected, (state, action) => {
        const messageId =
          (action.payload as any)?.messageId ?? (action.meta.arg as any)?.messageId
        if (!messageId) return
        state.taskSuggestions[messageId] = {
          loading: false,
          suggest: false,
          taskName: '',
          schedule: 'weekly',
          reason: '',
        }
      })
      // Pre-built tasks (Task Library)
      .addCase(fetchPreBuiltTasks.pending, (state) => {
        state.preBuiltTasksStatus = 'loading'
        state.preBuiltTasksError = null
      })
      .addCase(fetchPreBuiltTasks.fulfilled, (state, action) => {
        state.preBuiltTasksStatus = 'succeeded'
        state.preBuiltTasks = action.payload ?? []
      })
      .addCase(fetchPreBuiltTasks.rejected, (state, action) => {
        state.preBuiltTasksStatus = 'failed'
        state.preBuiltTasksError = action.payload || (action.error as any)?.message
      })
      .addCase(fetchPreBuiltTaskBySlug.pending, (state, action) => {
        const slug = action.meta.arg
        state.preBuiltTaskDetailStatus[slug] = 'loading'
      })
      .addCase(fetchPreBuiltTaskBySlug.fulfilled, (state, action) => {
        const { slug, task } = action.payload
        state.preBuiltTaskDetailStatus[slug] = 'succeeded'
        state.preBuiltTaskBySlug[slug] = task
      })
      .addCase(fetchPreBuiltTaskBySlug.rejected, (state, action) => {
        const slug = (action.payload as any)?.slug || action.meta.arg
        if (slug) state.preBuiltTaskDetailStatus[slug] = 'failed'
      })
      .addCase(installPreBuiltTask.pending, (state) => {
        state.preBuiltInstallStatus = 'installing'
        state.preBuiltInstallError = null
      })
      .addCase(installPreBuiltTask.fulfilled, (state) => {
        state.preBuiltInstallStatus = 'succeeded'
      })
      .addCase(installPreBuiltTask.rejected, (state, action) => {
        state.preBuiltInstallStatus = 'failed'
        state.preBuiltInstallError = action.payload || (action.error as any)?.message
      })
      .addCase(fetchAdminPreBuiltTasks.pending, (state) => {
        state.adminPreBuiltTasksStatus = 'loading'
      })
      .addCase(fetchAdminPreBuiltTasks.fulfilled, (state, action) => {
        state.adminPreBuiltTasksStatus = 'succeeded'
        state.adminPreBuiltTasks = action.payload ?? []
      })
      .addCase(fetchAdminPreBuiltTasks.rejected, (state) => {
        state.adminPreBuiltTasksStatus = 'failed'
      })
      .addCase(createPreBuiltTask.fulfilled, (state, action) => {
        if (action.payload) state.adminPreBuiltTasks = [action.payload, ...state.adminPreBuiltTasks]
      })
      .addCase(updatePreBuiltTask.fulfilled, (state, action) => {
        if (action.payload) {
          const idx = state.adminPreBuiltTasks.findIndex((t: any) => t.id === action.payload.id)
          if (idx >= 0) state.adminPreBuiltTasks[idx] = action.payload
        }
      })
      .addCase(deletePreBuiltTask.fulfilled, (state, action) => {
        state.adminPreBuiltTasks = state.adminPreBuiltTasks.filter((t: any) => t.id !== action.payload)
      })
      .addCase(draftPreBuiltTaskFromPrompt.pending, (state) => {
        state.adminPreBuiltTaskAiStatus = 'loading'
        state.adminPreBuiltTaskAiError = null
      })
      .addCase(draftPreBuiltTaskFromPrompt.fulfilled, (state) => {
        state.adminPreBuiltTaskAiStatus = 'succeeded'
      })
      .addCase(draftPreBuiltTaskFromPrompt.rejected, (state, action) => {
        state.adminPreBuiltTaskAiStatus = 'failed'
        state.adminPreBuiltTaskAiError = action.payload || (action.error as any)?.message
      })
      .addCase(enrichPreBuiltTaskFields.pending, (state) => {
        state.adminPreBuiltTaskAiStatus = 'loading'
        state.adminPreBuiltTaskAiError = null
      })
      .addCase(enrichPreBuiltTaskFields.fulfilled, (state) => {
        state.adminPreBuiltTaskAiStatus = 'succeeded'
      })
      .addCase(enrichPreBuiltTaskFields.rejected, (state, action) => {
        state.adminPreBuiltTaskAiStatus = 'failed'
        state.adminPreBuiltTaskAiError = action.payload || (action.error as any)?.message
      })
      .addCase(suggestPreBuiltTaskToolPipeline.pending, (state) => {
        state.adminPreBuiltTaskAiStatus = 'loading'
        state.adminPreBuiltTaskAiError = null
      })
      .addCase(suggestPreBuiltTaskToolPipeline.fulfilled, (state) => {
        state.adminPreBuiltTaskAiStatus = 'succeeded'
      })
      .addCase(suggestPreBuiltTaskToolPipeline.rejected, (state, action) => {
        state.adminPreBuiltTaskAiStatus = 'failed'
        state.adminPreBuiltTaskAiError = action.payload || (action.error as any)?.message
      })
      .addCase(generateExampleOutputFromRuns.pending, (state) => {
        state.adminPreBuiltTaskAiStatus = 'loading'
        state.adminPreBuiltTaskAiError = null
      })
      .addCase(generateExampleOutputFromRuns.fulfilled, (state) => {
        state.adminPreBuiltTaskAiStatus = 'succeeded'
      })
      .addCase(generateExampleOutputFromRuns.rejected, (state, action) => {
        state.adminPreBuiltTaskAiStatus = 'failed'
        state.adminPreBuiltTaskAiError = action.payload || (action.error as any)?.message
      })
      .addCase(fetchToolAccess.pending, (state) => {
        state.toolAccess.status = 'loading'
        state.toolAccess.error = null
      })
      .addCase(fetchToolAccess.fulfilled, (state, action) => {
        state.toolAccess.status = 'succeeded'
        state.toolAccess.invocations = action.payload?.invocations || []
        state.toolAccess.summary = action.payload?.summary || null
        if (action.payload?.rangeKey) state.toolAccess.rangeKey = action.payload.rangeKey
      })
      .addCase(fetchToolAccess.rejected, (state, action) => {
        state.toolAccess.status = 'failed'
        state.toolAccess.error = action.payload || (action.error as any)?.message
      })
  },
})

export const {
  toggleChatBox,
  setChatBoxExpanded,
  setMessageId,
  clearCurrentChat,
  toggleAgentMode,
  setAgentModeEnabled,
  setEffortLevel,
  setKnowledgeSources,
  clearKnowledgeSources,
  setGeneratedPdfs,
  clearGeneratedPdfs,
  pruneInsightBuckets,
  setAgentModel,
  setImageModel,
  addAgentStep,
  updateAgentStep,
  setAgentStatus,
  clearAgentSteps,
  setAgentSessionId,
  setAgentCreditSummary,
  resetInsightForSourcePage,
  markChatLoading,
  markChatIdle,
  upsertChatHistoryItem,
  setAgentMemoryUpdate,
  clearAgentMemoryUpdate,
  addAgentNarration,
  clearAgentNarrations,
  setAgentAvailableTools,
  setCsuiteReviewStatus,
  addCsuiteReviewStep,
  clearCsuiteReviews,
  setSelectedTaskId,
  dismissTaskSuggestion,
  clearTaskSuggestions,
  clearTaskIntentSynthesis,
  setActivePreBuiltTaskContext,
  clearActivePreBuiltTaskContext,
} = openaiSlice.actions

/**
 *
 * Selectors
 *
 */
export const selectOpenAIAccountStatus = (state) => state.openai.account.status || 'idle'
export const selectOpenAIAccountError = (state) => state.openai.account.error
export const selectOpenAIAccount = (state) => state.openai.account.data

export const selectDashboardConfig = (state) => state.root.user.dashboardconfig
export const selectRangeFilters = (state) => state.rangeFilters
export const selectKeywordFilters = (state) => state.keywordFilters
export const selectDashboardFilters = (state) => state.dashboard.filters

export const selectOpenAIChatHistoryStatus = (state) => state.openai.chatHistory.status || 'idle'
export const selectOpenAIChatHistory = (state) => state.openai.chatHistory.data || []
export const selectOpenAIChatHistoryHasMore = (state) => Boolean(state.openai.chatHistory.hasMore)
export const selectOpenAIChatHistoryLoadingMore = (state) => Boolean(state.openai.chatHistory.loadingMore)
export const selectOpenAIChatHistoryOffset = (state) => state.openai.chatHistory.offset || 0
export const selectOpenAIChatStatus = (state) => state.openai.chat.status || 'idle'

// Conversations with an in-progress turn (shared loading state for the history list)
const EMPTY_ACTIVE_CHAT_IDS: string[] = []
export const selectActiveChatIds = (state) => state.openai.activeChatIds || EMPTY_ACTIVE_CHAT_IDS

// Stable empty array reference to prevent selector memoization issues
const EMPTY_RELATED_QUESTIONS = []

export const selectRelatedQuestionsStatus = (state, sourcePage) =>
  state.openai.relatedQuestionsStatus[sourcePage] || 'idle'
export const selectRelatedQuestionsError = (state, sourcePage) =>
  state.openai.relatedQuestionsError[sourcePage]
export const selectRelatedQuestions = (state, sourcePage) =>
  state.openai.relatedQuestions[sourcePage] || EMPTY_RELATED_QUESTIONS

export const selectInsightStatus = (state, sourcePage) =>
  state.openai.insightStatus[sourcePage] || 'idle'
export const selectInsightError = (state, sourcePage) =>
  state.openai.insightError[sourcePage]
export const selectInsightResponse = (state, sourcePage) =>
  state.openai.insightResponse[sourcePage]
export const selectInsightResponseMessageId = (state) =>
  state.openai.messageId
export const selectAgentCreditSummary = (state, sourcePage) =>
  state.openai.agentCreditSummary?.[sourcePage] ?? null

// Agent mode selectors
export const selectAgentModeEnabled = (state) => state.openai.agentModeEnabled
export const selectEffortLevel = (state) => state.openai.effortLevel
export const selectAgentModel = (state) => state.openai.agentModel
export const selectImageModel = (state) => state.openai.imageModel

// Model/effort options. These read the server catalog (seeded with the bundled
// fallback), so components never import a hard-coded list.
export const selectAgentModelCatalog = (state) => state.openai.agentCatalog.data
export const selectAgentModelCatalogStatus = (state) => state.openai.agentCatalog.status || 'idle'
export const selectAgentModelOptions = (state) => state.openai.agentCatalog.data.models
export const selectEffortLevelOptions = (state) => state.openai.agentCatalog.data.effortLevels
export const selectDeepDiveOption = (state) => state.openai.agentCatalog.data.deepDive
/** Undefined against a papaya older than the roles payload — callers then use
 *  copy that names no model. */
export const selectModelRoles = (state) => state.openai.agentCatalog.data.modelRoles
export const selectImageModelOptions = (state) => state.openai.agentCatalog.data.imageModels
export const selectAgentSteps = (state, sourcePage) =>
  state.openai.agentSteps[sourcePage] || []
const EMPTY_KNOWLEDGE_SOURCES = []
export const selectKnowledgeSources = (state, sourcePage) =>
  state.openai.agentKnowledgeSources[sourcePage] || EMPTY_KNOWLEDGE_SOURCES
const EMPTY_GENERATED_PDFS = []
export const selectGeneratedPdfs = (state, sourcePage) =>
  state.openai.agentGeneratedPdfs[sourcePage] || EMPTY_GENERATED_PDFS
export const selectAgentStatus = (state, sourcePage) =>
  state.openai.agentStatus[sourcePage] || 'idle'
export const selectAgentCurrentStep = (state, sourcePage) =>
  state.openai.agentCurrentStep[sourcePage] || 0
export const selectAgentMemoryUpdate = (state, sourcePage) =>
  state.openai.agentMemoryUpdate[sourcePage] || null
export const selectAgentNarrations = (state, sourcePage) =>
  state.openai.agentNarrations[sourcePage] || []

// Stable empty array reference to prevent selector memoization issues
const EMPTY_TOOLS = []
export const selectAgentAvailableTools = (state, sourcePage) =>
  state.openai.agentAvailableTools[sourcePage] || EMPTY_TOOLS

// Chat preferences selectors for seamless conversation resumption
const EMPTY_INTERRUPTED_SESSIONS = []
export const selectChatPreferencesStatus = (state) => state.openai.chatPreferences.status || 'idle'
export const selectLastActiveChatId = (state) => state.openai.chatPreferences.lastActiveChatId
export const selectAutoLoadEnabled = (state) => state.openai.chatPreferences.autoLoadEnabled ?? true
export const selectInterruptedSessions = (state) =>
  state.openai.chatPreferences.interruptedSessions || EMPTY_INTERRUPTED_SESSIONS
export const selectChatPreferencesError = (state) => state.openai.chatPreferences.error

// C-Suite review selectors
const EMPTY_CSUITE_REVIEWS = []
export const selectCsuiteReviews = (state, sourcePage) =>
  state.openai.agentCsuiteReviews[sourcePage] || EMPTY_CSUITE_REVIEWS
export const selectCsuiteStatus = (state, sourcePage) =>
  state.openai.agentCsuiteStatus[sourcePage] || 'idle'

export const selectCustomTools = (state) => state.openai.customTools.items || []
export const selectCustomToolsStatus = (state) => state.openai.customTools.status || 'idle'
export const selectCustomToolsError = (state) => state.openai.customTools.error

// Scheduled tasks selectors
export const selectScheduledTasks = (state) => state.openai.scheduledTasks || []
export const selectScheduledTasksStatus = (state) => state.openai.scheduledTasksStatus || 'idle'
export const selectSelectedTaskId = (state) => state.openai.selectedTaskId
export const selectTaskRuns = (state, taskId) => state.openai.taskRuns[taskId] || { runs: [], total: 0 }
export const selectTaskRunsStatus = (state) => state.openai.taskRunsStatus || 'idle'
export const selectTaskIntentSynthesis = (state) => state.openai.taskIntentSynthesis
export const selectTaskIntentStatus = (state) => state.openai.taskIntentStatus || 'idle'
export const selectTaskIntentSynthesisKey = (state) => state.openai.taskIntentSynthesisKey ?? null
export const selectTaskIntentSynthesisInFlightKey = (state) => state.openai.taskIntentSynthesisInFlightKey ?? null

export const selectTaskSuggestions = (state) => state.openai.taskSuggestions || {}
export const selectTaskSuggestionForMessage = (state, messageId: string) =>
  state.openai.taskSuggestions?.[messageId] ?? null

// Pre-built tasks (Task Library) selectors
export const selectPreBuiltTasks = (state) => state.openai.preBuiltTasks || []
export const selectPreBuiltTasksStatus = (state) => state.openai.preBuiltTasksStatus || 'idle'
export const selectPreBuiltTasksError = (state) => state.openai.preBuiltTasksError
export const selectPreBuiltTaskBySlug = (state, slug: string) =>
  state.openai.preBuiltTaskBySlug?.[slug] || null
export const selectPreBuiltTaskDetailStatus = (state, slug: string) =>
  state.openai.preBuiltTaskDetailStatus?.[slug] || 'idle'
export const selectPreBuiltInstallStatus = (state) => state.openai.preBuiltInstallStatus || 'idle'
export const selectPreBuiltInstallError = (state) => state.openai.preBuiltInstallError
export const selectActivePreBuiltTaskContext = (state) =>
  state.openai.activePreBuiltTaskContext || null

// Admin Pre-built tasks selectors
export const selectAdminPreBuiltTasks = (state) => state.openai.adminPreBuiltTasks || []
export const selectAdminPreBuiltTasksStatus = (state) =>
  state.openai.adminPreBuiltTasksStatus || 'idle'
export const selectAdminPreBuiltTaskAiStatus = (state) =>
  state.openai.adminPreBuiltTaskAiStatus || 'idle'
export const selectAdminPreBuiltTaskAiError = (state) => state.openai.adminPreBuiltTaskAiError

export const selectToolAccessInvocations = (state) => state.openai.toolAccess.invocations || []
export const selectToolAccessSummary = (state) => state.openai.toolAccess.summary
export const selectToolAccessRangeKey = (state) => state.openai.toolAccess.rangeKey || '30d'
export const selectToolAccessStatus = (state) => state.openai.toolAccess.status || 'idle'
export const selectToolAccessError = (state) => state.openai.toolAccess.error

const TOOL_WRITE_PREFIXES = ['modify_', 'update_', 'set_', 'enable_', 'disable_', 'configure_', 'create_', 'delete_', 'upload_', 'remove_']
const TOOL_WRITE_SET = new Set(['insure_shipment', 'void_insurance', 'export_to_email', 'export_to_google_drive', 'export_to_csv'])
const isMcpStep = (name: string) => name.startsWith('mcp__payhelm__') || name.startsWith('mcp__custom__') || name.startsWith('payhelm__')
const stripToolPrefix = (name: string) => name.replace(/^mcp__payhelm__/, '').replace(/^mcp__custom__/, '').replace(/^payhelm__/, '').toLowerCase()
const isWriteToolName = (name: string) => TOOL_WRITE_PREFIXES.some(p => name.startsWith(p)) || TOOL_WRITE_SET.has(name)

// Derived from chatHistory already in Redux — no extra API call needed for sidebar badges
export const selectToolAccessSummaryByChatId = createSelector(
  selectOpenAIChatHistory,
  (chatHistory) => {
    const map: Record<string, { total: number; writes: number; failed: number }> = {}
    for (const chat of chatHistory) {
      let analytics: any
      try {
        analytics = typeof (chat as any).analytics === 'string'
          ? JSON.parse((chat as any).analytics)
          : (chat as any).analytics
      } catch { continue }
      if (!analytics?.agentStepsByTurn) continue
      const summary = { total: 0, writes: 0, failed: 0 }
      for (const steps of Object.values(analytics.agentStepsByTurn)) {
        if (!Array.isArray(steps)) continue
        for (const step of steps as any[]) {
          if (!step.tool || !isMcpStep(step.tool) || step.status === 'running') continue
          const toolName = stripToolPrefix(step.tool)
          summary.total++
          if (isWriteToolName(toolName)) summary.writes++
          if (step.status === 'failed') summary.failed++
        }
      }
      if (summary.total > 0) map[(chat as any).message_id] = summary
    }
    return map
  }
)

export default openaiSlice.reducer
